import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// ─── Documentation data structure ───
const SECTIONS = [
  {
    group: 'Getting Started',
    icon: 'rocket',
    items: [
      { id: 'overview', title: 'Product Overview' },
      { id: 'key-features', title: 'Key Features' },
      { id: 'quick-start', title: 'Quick Start' },
    ],
  },
  {
    group: 'Chrome Extension',
    icon: 'chrome',
    items: [
      { id: 'ext-install', title: 'Installation' },
      { id: 'ext-login', title: 'Login' },
      { id: 'ext-recording', title: 'Recording a Guide' },
      { id: 'ext-modes', title: 'Recording Modes' },
      { id: 'ext-floating-panel', title: 'Floating Panel' },
      { id: 'ext-side-panel', title: 'Side Panel Preview' },
      { id: 'ext-voice', title: 'Voice Narration' },
      { id: 'ext-insert', title: 'Inserting Steps' },
      { id: 'ext-events', title: 'Captured Events' },
      { id: 'ext-capture-settings', title: 'Capture Settings' },
    ],
  },
  {
    group: 'Desktop App',
    icon: 'desktop',
    items: [
      { id: 'desktop-overview', title: 'Overview & Installation' },
      { id: 'desktop-recording', title: 'Recording Flow' },
      { id: 'desktop-sync', title: 'Offline & Sync' },
    ],
  },
  {
    group: 'Meeting Mode',
    icon: 'video',
    items: [
      { id: 'meeting-overview', title: 'Overview' },
      { id: 'meeting-output', title: 'What Gets Generated' },
      { id: 'meeting-workflows', title: 'Sample Workflows' },
      { id: 'meeting-plans', title: 'Plan Availability' },
    ],
  },
  {
    group: 'Import Recording',
    icon: 'upload',
    items: [
      { id: 'import-overview', title: 'Overview' },
      { id: 'import-upload', title: 'Uploading a Recording' },
      { id: 'import-transcript', title: 'Optional Transcript' },
      { id: 'import-review', title: 'Segment Review' },
    ],
  },
  {
    group: 'Web Dashboard',
    icon: 'dashboard',
    items: [
      { id: 'dash-login', title: 'Login & Signup' },
      { id: 'dash-home', title: 'Dashboard Home' },
      { id: 'dash-guide-detail', title: 'Guide Detail View' },
      { id: 'dash-manual-steps', title: 'Manual Step Types' },
      { id: 'dash-annotations', title: 'Screenshot Annotations' },
      { id: 'dash-workflow', title: 'Workflow Flowchart' },
      { id: 'dash-projects', title: 'Projects & Folders' },
      { id: 'dash-favorites', title: 'Favorites & Recent Guides' },
      { id: 'dash-sharing', title: 'Guide Sharing' },
      { id: 'dash-comments', title: 'Comments' },
      { id: 'dash-improvements', title: 'Improvements & Suggestions' },
      { id: 'dash-ask-insta', title: 'Ask Insta Chat' },
      { id: 'dash-export', title: 'Exporting Guides' },
      { id: 'dash-embed', title: 'Embedding Guides' },
      { id: 'dash-profile', title: 'Profile & Account' },
      { id: 'dash-2fa', title: 'Two-Factor Authentication' },
    ],
  },
  {
    group: 'Organization',
    icon: 'building',
    items: [
      { id: 'org-setup', title: 'Organization Setup' },
      { id: 'org-members', title: 'Members & Invitations' },
      { id: 'org-roles', title: 'Roles & Permissions' },
      { id: 'org-projects', title: 'Project Management' },
      { id: 'org-ai', title: 'AI Provider Settings' },
      { id: 'org-redaction', title: 'Sensitive Data Redaction' },
      { id: 'org-storage', title: 'Storage Provider (Enterprise)' },
    ],
  },
  {
    group: 'Billing & Subscriptions',
    icon: 'creditcard',
    items: [
      { id: 'billing-plans', title: 'Plans & Pricing' },
      { id: 'billing-upgrade', title: 'Upgrading Your Plan' },
      { id: 'billing-portal', title: 'Managing Your Subscription' },
    ],
  },
  {
    group: 'Help & Reference',
    icon: 'lifebuoy',
    items: [
      { id: 'keyboard-shortcuts', title: 'Keyboard Shortcuts' },
      { id: 'troubleshooting', title: 'Troubleshooting' },
      { id: 'faq', title: 'FAQ' },
    ],
  },
];

// Group icons as inline SVGs (16x16)
const GROUP_ICONS = {
  rocket: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>,
  chrome: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="21.17" y1="8" x2="12" y2="8"/><line x1="3.95" y1="6.06" x2="8.54" y2="14"/><line x1="10.88" y1="21.94" x2="15.46" y2="14"/></svg>,
  desktop: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  dashboard: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg>,
  building: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="1"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01"/></svg>,
  creditcard: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  lifebuoy: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="4.93" y1="4.93" x2="9.17" y2="9.17"/><line x1="14.83" y1="14.83" x2="19.07" y2="19.07"/><line x1="14.83" y1="9.17" x2="19.07" y2="4.93"/><line x1="4.93" y1="19.07" x2="9.17" y2="14.83"/></svg>,
  video: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>,
  upload: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
};

// h3 sub-sections for "On This Page" panel
const SUB_SECTIONS = {
  'overview': [
    { id: 'overview-how-it-works', title: 'How It Works' },
  ],
  'ext-install': [
    { id: 'ext-install-chrome-web-store', title: 'From the Chrome Web Store' },
  ],
  'ext-events': [
    { id: 'ext-events-click', title: 'Click Events' },
    { id: 'ext-events-keyboard', title: 'Keyboard Events' },
    { id: 'ext-events-scroll', title: 'Scroll Events' },
  ],
  'desktop-overview': [
    { id: 'desktop-overview-features', title: 'Key Features' },
    { id: 'desktop-overview-install', title: 'Installation' },
  ],
  'desktop-recording': [
    { id: 'desktop-recording-target', title: 'Target Selection' },
    { id: 'desktop-recording-modes', title: 'Recording Modes' },
  ],
  'dash-login': [
    { id: 'dash-login-creating', title: 'Creating an Account' },
    { id: 'dash-login-logging-in', title: 'Logging In' },
  ],
  'dash-guide-detail': [
    { id: 'dash-guide-detail-step-cards', title: 'Step Cards' },
    { id: 'dash-guide-detail-step-actions', title: 'Step Actions' },
  ],
  'dash-workflow': [
    { id: 'dash-workflow-layout-options', title: 'Layout Options' },
    { id: 'dash-workflow-features', title: 'Flowchart Features' },
  ],
  'dash-projects': [
    { id: 'dash-projects-explorer', title: 'Projects Explorer' },
    { id: 'dash-projects-managing', title: 'Managing Projects' },
  ],
  'org-members': [
    { id: 'org-members-inviting', title: 'Inviting Members' },
    { id: 'org-members-managing', title: 'Managing Members' },
  ],
  'org-roles': [
    { id: 'org-roles-predefined', title: 'Predefined Roles' },
    { id: 'org-roles-custom', title: 'Custom Roles' },
  ],
  'dash-manual-steps': [
    { id: 'dash-manual-steps-rich-text', title: 'Rich Text' },
    { id: 'dash-manual-steps-callout', title: 'Callout' },
    { id: 'dash-manual-steps-image', title: 'Image' },
    { id: 'dash-manual-steps-audio', title: 'Audio Note' },
    { id: 'dash-manual-steps-section', title: 'Section Divider' },
  ],
  'org-storage': [
    { id: 'org-storage-providers', title: 'Supported Providers' },
    { id: 'org-storage-configure', title: 'How to Configure' },
    { id: 'org-storage-bucket-policy', title: 'Bucket Permissions' },
  ],
  'dash-2fa': [
    { id: 'dash-2fa-setup', title: 'First-Time Setup' },
    { id: 'dash-2fa-backup-codes', title: 'Backup Codes' },
    { id: 'dash-2fa-recovery', title: 'Lost Your Authenticator?' },
    { id: 'dash-2fa-disable', title: 'Disabling 2FA' },
  ],
  'billing-plans': [
    { id: 'billing-plans-personal', title: 'Personal Plans' },
    { id: 'billing-plans-business', title: 'Business (Organizations)' },
    { id: 'billing-plans-enterprise', title: 'Enterprise (Organizations)' },
    { id: 'billing-plans-trial', title: 'Free trials' },
  ],
  'billing-upgrade': [
    { id: 'billing-upgrade-checkout', title: 'Stripe Checkout' },
    { id: 'billing-upgrade-banner', title: 'Reminder Banner' },
  ],
  'billing-portal': [
    { id: 'billing-portal-launch', title: 'Opening the Portal' },
    { id: 'billing-portal-cancel', title: 'Canceling a Subscription' },
  ],
};

// Flatten all section items for search
const ALL_ITEMS = SECTIONS.flatMap(s => s.items.map(item => ({ ...item, group: s.group })));

// ─── Simple markdown renderer for AI chat ───
function renderMarkdown(text) {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
    .replace(/^\s*[-*]\s+(.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .replace(/\n/g, '<br/>');
  return html;
}

export default function DocumentationPage() {
  const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3001';
  const token = localStorage.getItem('authToken');

  // ─── State ───
  const [activeSection, setActiveSection] = useState('overview');
  // All groups start collapsed except the first one
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const initial = {};
    SECTIONS.forEach((s, i) => { if (i > 0) initial[s.group] = true; });
    return initial;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [askInstaOpen, setAskInstaOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [faqOpen, setFaqOpen] = useState({});

  const mainContentRef = useRef(null);
  const searchInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const sectionRefs = useRef({});

  // ─── Search content map (built once) ───
  const searchContent = useMemo(() => {
    // We build a simple text index from section titles and group names
    return ALL_ITEMS.map(item => ({
      id: item.id,
      title: item.title,
      group: item.group,
      text: `${item.group} ${item.title}`.toLowerCase(),
    }));
  }, []);

  // ─── Find which group the active section belongs to ───
  const activeGroup = useMemo(() => {
    for (const section of SECTIONS) {
      if (section.items.some(item => item.id === activeSection)) return section.group;
    }
    return SECTIONS[0].group;
  }, [activeSection]);

  // Auto-expand the active group when scrollspy changes the active section
  useEffect(() => {
    if (activeGroup && collapsedGroups[activeGroup]) {
      setCollapsedGroups(prev => ({ ...prev, [activeGroup]: false }));
    }
  }, [activeGroup]);

  // ─── Search handler ───
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }
    const q = searchQuery.toLowerCase();
    const results = searchContent.filter(item => item.text.includes(q));
    setSearchResults(results);
    setShowSearchDropdown(results.length > 0);
  }, [searchQuery, searchContent]);

  // ─── Scrollspy with IntersectionObserver ───
  useEffect(() => {
    const observers = [];
    const options = { rootMargin: '-80px 0px -60% 0px', threshold: 0 };

    const callback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);

    ALL_ITEMS.forEach(item => {
      const el = document.getElementById(item.id);
      if (el) {
        observer.observe(el);
        observers.push(el);
      }
    });

    return () => {
      observers.forEach(el => observer.unobserve(el));
    };
  }, []);

  // ─── Scroll chat to bottom ───
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatLoading]);

  // ─── Navigation click ───
  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setSearchQuery('');
    setShowSearchDropdown(false);
  }, []);

  // ─── Toggle TOC group ───
  const toggleGroup = useCallback((group) => {
    setCollapsedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  }, []);

  // ─── FAQ toggle ───
  const toggleFaq = useCallback((idx) => {
    setFaqOpen(prev => ({ ...prev, [idx]: !prev[idx] }));
  }, []);

  // ─── Ask Insta chat ───
  const sendChatMessage = useCallback(async () => {
    if (!chatInput.trim() || chatLoading) return;
    const question = chatInput.trim();
    const newMessages = [...chatMessages, { role: 'user', content: question }];
    setChatMessages(newMessages);
    setChatInput('');
    setChatLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/docs/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question,
          conversationHistory: newMessages.slice(-10),
        }),
      });
      const data = await res.json();
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.answer || data.message || 'Sorry, I could not process that request.' }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  }, [chatInput, chatLoading, chatMessages, API_URL, token]);

  // ─── Close search dropdown on outside click ───
  useEffect(() => {
    const handler = (e) => {
      if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ─── Highlight match in search result ───
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="doc-search-highlight">{text.slice(idx, idx + query.length)}</span>
        {text.slice(idx + query.length)}
      </>
    );
  };

  return (
    <div className="doc-root">
      <style>{`
        /* ══════════════════════════════════════════════
           DOCUMENTATION PAGE — doc- prefixed classes
           ══════════════════════════════════════════════ */
        .doc-root {
          display: flex;
          min-height: 100vh;
          background: #f8fafc;
          font-family: inherit;
          color: #1e293b;
          line-height: 1.7;
          font-size: 14px;
          position: relative;
        }

        /* ── Left TOC Sidebar ── */
        .doc-sidebar {
          width: 240px;
          min-width: 240px;
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
          position: sticky;
          top: 0;
          height: 100vh;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          z-index: 20;
          flex-shrink: 0;
        }
        .doc-sidebar::-webkit-scrollbar { width: 4px; }
        .doc-sidebar::-webkit-scrollbar-track { background: transparent; }
        .doc-sidebar::-webkit-scrollbar-thumb { background: rgba(255,255,255,.1); border-radius: 4px; }

        .doc-sidebar-header {
          padding: 20px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          flex-shrink: 0;
        }
        .doc-sidebar-logo {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .doc-sidebar-logo-icon {
          flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
        }
        .doc-sidebar-logo-icon img { display: block; }
        .doc-sidebar-logo-text {
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          letter-spacing: -0.3px;
        }
        .doc-sidebar-logo-sub {
          font-size: 11px;
          color: #94a3b8;
          margin-top: 1px;
        }

        .doc-sidebar-nav {
          padding: 12px 8px;
          flex: 1;
        }
        .doc-nav-group {
          margin-bottom: 2px;
        }
        .doc-nav-group-header {
          padding: 9px 12px;
          font-size: 13px;
          font-weight: 500;
          color: #94a3b8;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          user-select: none;
          transition: all 0.15s;
          border-radius: 8px;
          margin: 0 0 1px;
        }
        .doc-nav-group-header:hover {
          color: #e2e8f0;
          background: rgba(255,255,255,0.04);
        }
        .doc-nav-group-header.active-group {
          color: #e2e8f0;
        }
        .doc-nav-group-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 7px;
          background: rgba(255,255,255,0.06);
          flex-shrink: 0;
          color: #64748b;
          transition: all 0.2s;
        }
        .doc-nav-group-header:hover .doc-nav-group-icon,
        .doc-nav-group-header.active-group .doc-nav-group-icon {
          background: rgba(99,102,241,0.2);
          color: #a5b4fc;
        }
        .doc-nav-group-label {
          flex: 1;
          min-width: 0;
        }
        .doc-nav-group-arrow {
          font-size: 10px;
          transition: transform 0.25s ease;
          opacity: 0.5;
          flex-shrink: 0;
        }
        .doc-nav-group-arrow.collapsed {
          transform: rotate(-90deg);
        }
        .doc-nav-group-count {
          font-size: 11px;
          color: rgba(148,163,184,0.4);
          margin-left: auto;
          margin-right: 4px;
        }
        .doc-nav-items {
          overflow: hidden;
          transition: max-height 0.3s ease, opacity 0.25s ease;
          max-height: 600px;
          opacity: 1;
          padding-left: 20px;
          border-left: 1px solid rgba(255,255,255,0.06);
          margin-left: 25px;
          margin-bottom: 4px;
        }
        .doc-nav-items.collapsed {
          max-height: 0;
          opacity: 0;
          margin-bottom: 0;
        }
        .doc-nav-link {
          display: block;
          padding: 6px 12px;
          color: #94a3b8;
          text-decoration: none;
          font-size: 13px;
          font-weight: 400;
          border-radius: 6px;
          transition: all 0.15s ease;
          margin-bottom: 1px;
          cursor: pointer;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          font-family: inherit;
        }
        .doc-nav-link:hover {
          background: rgba(255,255,255,0.05);
          color: #e2e8f0;
        }
        .doc-nav-link.active {
          background: rgba(99,102,241,0.15);
          color: #c7d2fe;
          font-weight: 500;
        }

        .doc-sidebar-version {
          padding: 16px;
          border-top: 1px solid rgba(255,255,255,0.08);
          font-size: 11px;
          color: rgba(148,163,184,0.4);
          flex-shrink: 0;
        }

        /* ── Main Content Area ── */
        .doc-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .doc-main-inner {
          width: 100%;
          max-width: 860px;
          padding: 0 32px 64px;
        }

        /* ── Search Bar ── */
        .doc-search-wrapper {
          position: sticky;
          top: 0;
          z-index: 15;
          background: #f8fafc;
          padding: 16px 0 12px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .doc-search-container {
          position: relative;
          flex: 1;
        }
        .doc-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          pointer-events: none;
        }
        .doc-search-input {
          width: 100%;
          padding: 10px 14px 10px 40px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          font-size: 14px;
          font-family: inherit;
          background: #fff;
          color: #1e293b;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .doc-search-input:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
        }
        .doc-search-input::placeholder {
          color: #94a3b8;
        }
        .doc-search-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0; right: 0;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          max-height: 320px;
          overflow-y: auto;
          z-index: 100;
          animation: doc-fadeSlideIn 0.15s ease;
        }
        .doc-search-result {
          padding: 10px 16px;
          cursor: pointer;
          transition: background 0.1s;
          border-bottom: 1px solid #f1f5f9;
        }
        .doc-search-result:last-child { border-bottom: none; }
        .doc-search-result:hover {
          background: #f8fafc;
        }
        .doc-search-result-title {
          font-size: 14px;
          font-weight: 500;
          color: #1e293b;
        }
        .doc-search-result-breadcrumb {
          font-size: 12px;
          color: #94a3b8;
          margin-top: 2px;
        }
        .doc-search-highlight {
          background: rgba(99,102,241,0.15);
          color: #4f46e5;
          border-radius: 2px;
          padding: 0 1px;
        }

        /* ── Ask Insta Button ──
           Purple gradient + chat-bubble + sparkle composite icon — matches
           the main app's Ask Insta widget so the assistant has a consistent
           visual identity across in-app docs and docs.instaguide.ai. */
        .doc-ask-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: #fff;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          white-space: nowrap;
          transition: box-shadow 0.2s, transform 0.15s, filter 0.15s;
          box-shadow: 0 2px 8px rgba(124,58,237,0.3);
          flex-shrink: 0;
        }
        .doc-ask-btn:hover {
          box-shadow: 0 4px 16px rgba(124,58,237,0.45);
          transform: translateY(-1px);
          filter: brightness(1.06);
        }
        .doc-ask-iconwrap {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .doc-ask-sparkle {
          position: absolute;
          top: -3px;
          right: -4px;
          filter: drop-shadow(0 0 1.5px rgba(124,58,237,0.95));
        }

        /* ── Ask Insta Panel ── */
        .doc-ask-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.3);
          z-index: 50;
          animation: doc-fadeIn 0.2s ease;
        }
        .doc-ask-panel {
          position: fixed;
          top: 0; right: 0; bottom: 0;
          width: 400px;
          background: #fff;
          box-shadow: -4px 0 24px rgba(0,0,0,0.12);
          z-index: 51;
          display: flex;
          flex-direction: column;
          animation: doc-slideInRight 0.25s ease;
        }
        .doc-ask-header {
          padding: 20px;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }
        .doc-ask-header-left {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-weight: 700;
        }
        .doc-ask-close {
          background: rgba(255,255,255,0.2);
          border: none;
          color: #fff;
          width: 32px; height: 32px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          transition: background 0.15s;
        }
        .doc-ask-close:hover {
          background: rgba(255,255,255,0.3);
        }
        .doc-ask-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .doc-ask-msg {
          max-width: 85%;
          padding: 10px 14px;
          border-radius: 12px;
          font-size: 14px;
          line-height: 1.6;
          word-break: break-word;
        }
        .doc-ask-msg.user {
          align-self: flex-end;
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          color: #fff;
          border-bottom-right-radius: 4px;
        }
        .doc-ask-msg.assistant {
          align-self: flex-start;
          background: #f1f5f9;
          color: #1e293b;
          border-bottom-left-radius: 4px;
        }
        .doc-ask-msg.assistant code {
          font-size: 13px;
          background: #e2e8f0;
          padding: 1px 5px;
          border-radius: 4px;
        }
        .doc-ask-msg.assistant ul {
          margin: 6px 0 6px 18px;
          padding: 0;
        }
        .doc-ask-msg.assistant li {
          margin-bottom: 4px;
        }
        .doc-ask-typing {
          align-self: flex-start;
          padding: 10px 14px;
          background: #f1f5f9;
          border-radius: 12px;
          display: flex;
          gap: 4px;
        }
        .doc-ask-typing-dot {
          width: 6px; height: 6px;
          background: #94a3b8;
          border-radius: 50%;
          animation: doc-typingBounce 1.2s ease infinite;
        }
        .doc-ask-typing-dot:nth-child(2) { animation-delay: 0.2s; }
        .doc-ask-typing-dot:nth-child(3) { animation-delay: 0.4s; }
        .doc-ask-input-bar {
          padding: 12px 16px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          gap: 8px;
          flex-shrink: 0;
        }
        .doc-ask-input {
          flex: 1;
          padding: 10px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 14px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s;
        }
        .doc-ask-input:focus {
          border-color: #6366f1;
        }
        .doc-ask-send {
          padding: 10px 16px;
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          color: #fff;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          font-size: 14px;
          transition: opacity 0.15s;
          flex-shrink: 0;
        }
        .doc-ask-send:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* ── Right Panel (On This Page) ── */
        .doc-right-panel {
          width: 220px;
          min-width: 220px;
          position: sticky;
          top: 80px;
          height: fit-content;
          max-height: calc(100vh - 100px);
          overflow-y: auto;
          padding: 16px 0 16px 20px;
          flex-shrink: 0;
          border-left: 1px solid #e2e8f0;
        }
        .doc-right-panel::-webkit-scrollbar { width: 3px; }
        .doc-right-panel::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 3px; }
        @media (max-width: 1200px) {
          .doc-right-panel { display: none; }
        }
        .doc-right-title {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #94a3b8;
          margin-bottom: 8px;
          padding-left: 12px;
        }
        .doc-right-section-title {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #94a3b8;
          margin: 16px 0 6px;
          padding-left: 12px;
        }
        .doc-right-link {
          display: block;
          padding: 5px 12px;
          font-size: 13px;
          color: #64748b;
          text-decoration: none;
          cursor: pointer;
          border: none;
          background: none;
          text-align: left;
          font-family: inherit;
          transition: all 0.15s;
          border-radius: 6px;
          border-left: 2px solid transparent;
          margin-left: 0;
          width: 100%;
        }
        .doc-right-link:hover {
          color: #6366f1;
          background: rgba(99,102,241,0.04);
        }
        .doc-right-link.active {
          color: #6366f1;
          font-weight: 500;
          border-left-color: #6366f1;
          background: rgba(99,102,241,0.06);
        }
        .doc-right-link.sub {
          padding-left: 20px;
          font-size: 12px;
          color: #94a3b8;
        }
        .doc-right-link.sub:hover {
          color: #6366f1;
        }
        .doc-right-link.sub.active {
          color: #6366f1;
          border-left-color: transparent;
          background: none;
          font-weight: 500;
        }
        .doc-right-divider {
          height: 1px;
          background: #e2e8f0;
          margin: 12px 0;
        }

        /* ── Section Typography ── */
        .doc-section-heading {
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
          margin: 48px 0 14px;
          padding-bottom: 12px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          gap: 12px;
          letter-spacing: -0.4px;
          scroll-margin-top: 80px;
        }
        .doc-section-icon {
          width: 32px; height: 32px;
          border-radius: 8px;
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .doc-section-icon svg {
          width: 16px; height: 16px;
        }
        .doc-h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 28px 0 10px;
          color: #1e293b;
          scroll-margin-top: 80px;
        }
        .doc-h4 {
          font-size: 14px;
          font-weight: 600;
          margin: 20px 0 6px;
          color: #1e293b;
        }
        .doc-p {
          margin-bottom: 12px;
          color: #475569;
          font-size: 14px;
        }
        .doc-p strong, .doc-li strong { color: #1e293b; }
        .doc-ul, .doc-ol {
          margin: 0 0 14px 20px;
          color: #475569;
        }
        .doc-li {
          margin-bottom: 5px;
          font-size: 14px;
        }
        .doc-code {
          font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace;
          font-size: 13px;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          padding: 2px 7px;
          border-radius: 6px;
          color: #6366f1;
        }

        /* ── Cards ── */
        .doc-card {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          border: 1px solid #e2e8f0;
          padding: 24px;
          margin: 14px 0;
          transition: all 0.25s ease;
        }
        .doc-card:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.08);
          border-color: #cbd5e1;
        }
        .doc-card .doc-h4 { margin-top: 0; }
        .doc-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 16px;
          margin: 14px 0;
        }

        /* ── Feature Cards ── */
        .doc-feature-card {
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          transition: all 0.25s ease;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: flex-start;
        }
        .doc-feature-card:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.08);
          border-color: #cbd5e1;
        }
        .doc-feature-card-top {
          padding: 20px 0 20px 20px;
          display: flex;
          align-items: flex-start;
          flex-shrink: 0;
        }
        .doc-feature-icon {
          width: 40px; height: 40px;
          border-radius: 10px;
          background: linear-gradient(135deg, rgba(99,102,241,0.08), rgba(79,70,229,0.08));
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6366f1;
        }
        .doc-feature-icon svg { width: 20px; height: 20px; }
        .doc-feature-card-body {
          padding: 18px 20px 18px 14px;
        }
        .doc-feature-card-body h4 {
          margin: 0 0 4px;
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
        }
        .doc-feature-card-body p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
          line-height: 1.6;
        }

        /* ── Callouts ── */
        .doc-callout {
          padding: 14px 18px;
          border-radius: 10px;
          margin: 16px 0;
          font-size: 13px;
          color: #475569;
          background: #eff6ff;
          border: 1px solid #bfdbfe;
        }
        .doc-callout strong {
          display: block;
          margin-bottom: 3px;
          font-size: 13px;
          font-weight: 600;
        }
        .doc-callout.info { background: #eff6ff; border-color: #bfdbfe; }
        .doc-callout.info strong { color: #1e40af; }
        .doc-callout.warning { background: #fffbeb; border-color: #fde68a; }
        .doc-callout.warning strong { color: #92400e; }
        .doc-callout.success { background: #ecfdf5; border-color: #a7f3d0; }
        .doc-callout.success strong { color: #065f46; }
        .doc-callout.danger { background: #fef2f2; border-color: #fecaca; }
        .doc-callout.danger strong { color: #991b1b; }

        /* ── Numbered Steps ── */
        .doc-steps {
          counter-reset: step;
          list-style: none;
          margin-left: 0;
          padding-left: 0;
        }
        .doc-steps li {
          counter-increment: step;
          position: relative;
          padding: 14px 0 14px 48px;
          margin-bottom: 0;
          border-left: 2px solid #e2e8f0;
          margin-left: 14px;
          color: #475569;
          font-size: 14px;
        }
        .doc-steps li:last-child { border-left-color: transparent; }
        .doc-steps li::before {
          content: counter(step);
          position: absolute;
          left: -13px;
          top: 12px;
          width: 24px; height: 24px;
          border-radius: 50%;
          background: #fff;
          color: #6366f1;
          border: 2px solid #6366f1;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 12px;
        }

        /* ── Table ── */
        .doc-table {
          width: 100%;
          border-collapse: collapse;
          margin: 14px 0;
          font-size: 13px;
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .doc-table th {
          padding: 12px 16px;
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #64748b;
          background: #f1f5f9;
          border-bottom: 1px solid #e2e8f0;
        }
        .doc-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #e2e8f0;
          color: #475569;
          vertical-align: top;
        }
        .doc-table tr:last-child td { border-bottom: none; }
        .doc-table tr:hover td { background: #f8fafc; }

        /* ── Keyboard Badges ── */
        .doc-kbd {
          display: inline-block;
          padding: 2px 8px;
          font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace;
          font-size: 12px;
          font-weight: 500;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          box-shadow: 0 1px 0 #e2e8f0;
          color: #1e293b;
        }

        /* ── Event Badges ── */
        .doc-event-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }
        .doc-event-badge.click { background: #f5f3ff; color: #7c3aed; }
        .doc-event-badge.keyboard { background: #fef3c7; color: #d97706; }
        .doc-event-badge.scroll { background: #ecfdf5; color: #059669; }

        /* ── Plan Cards ── */
        .doc-plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 12px;
          margin: 16px 0;
        }
        .doc-plan-card {
          background: #fff;
          border-radius: 12px;
          padding: 24px 16px;
          text-align: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          transition: all 0.25s ease;
          border: 1px solid #e2e8f0;
        }
        .doc-plan-card:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.08);
          border-color: #cbd5e1;
        }
        .doc-plan-card.highlighted {
          border-color: #6366f1;
          background: linear-gradient(135deg, rgba(99,102,241,0.08), rgba(79,70,229,0.08));
        }
        .doc-plan-card h4 {
          margin: 0 0 4px;
          font-size: 11px;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .doc-plan-card .doc-plan-limit {
          font-size: 36px;
          font-weight: 700;
          color: #1e293b;
          margin: 8px 0;
        }
        .doc-plan-card .doc-plan-sublabel {
          font-size: 12px;
          color: #64748b;
        }

        /* ── FAQ Accordion ── */
        .doc-faq-item {
          background: #fff;
          border-radius: 12px;
          margin: 6px 0;
          overflow: hidden;
          transition: all 0.25s;
          border: 1px solid #e2e8f0;
        }
        .doc-faq-item:hover { border-color: #cbd5e1; }
        .doc-faq-item.open { border-color: #6366f1; background: #fafbff; }
        .doc-faq-question {
          padding: 16px 20px;
          font-weight: 500;
          font-size: 14px;
          color: #1e293b;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          user-select: none;
          transition: background 0.15s;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          font-family: inherit;
        }
        .doc-faq-question:hover { background: #f8fafc; }
        .doc-faq-icon {
          font-size: 18px;
          font-weight: 300;
          color: #94a3b8;
          transition: transform 0.3s ease, color 0.3s;
          flex-shrink: 0;
        }
        .doc-faq-item.open .doc-faq-icon {
          transform: rotate(45deg);
          color: #6366f1;
        }
        .doc-faq-answer {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.4s ease;
        }
        .doc-faq-item.open .doc-faq-answer {
          max-height: 300px;
        }
        .doc-faq-answer-inner {
          padding: 0 20px 16px;
          font-size: 13px;
          color: #475569;
          line-height: 1.7;
        }

        /* ── Trouble Cards ── */
        .doc-trouble-card {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          padding: 20px 24px;
          margin: 10px 0;
          transition: all 0.2s;
          border: 1px solid #e2e8f0;
        }
        .doc-trouble-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        .doc-trouble-card .doc-h4 { margin: 0 0 8px; }

        /* ── Animations ── */
        @keyframes doc-fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes doc-fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes doc-slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes doc-typingBounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .doc-sidebar { display: none; }
          .doc-main-inner { padding: 0 16px 48px; }
        }
      `}</style>

      {/* ═══════════ LEFT TOC SIDEBAR ═══════════ */}
      <div className="doc-sidebar">
        <div className="doc-sidebar-header">
          <div className="doc-sidebar-logo">
            <div className="doc-sidebar-logo-icon">
              <img src="/favicon.png" alt="InstaGuide" width="36" height="36" style={{ borderRadius: 8 }} />
            </div>
            <div>
              <div className="doc-sidebar-logo-text">InstaGuide.ai</div>
              <div className="doc-sidebar-logo-sub">Documentation</div>
            </div>
          </div>
        </div>

        <div className="doc-sidebar-nav">
          {SECTIONS.map(section => (
            <div className="doc-nav-group" key={section.group}>
              <div
                className={`doc-nav-group-header ${activeGroup === section.group ? 'active-group' : ''}`}
                onClick={() => toggleGroup(section.group)}
              >
                <span className="doc-nav-group-icon">
                  {GROUP_ICONS[section.icon]}
                </span>
                <span className="doc-nav-group-label">{section.group}</span>
                <span className="doc-nav-group-count">{section.items.length}</span>
                <span className={`doc-nav-group-arrow ${collapsedGroups[section.group] ? 'collapsed' : ''}`}>
                  &#9662;
                </span>
              </div>
              <div className={`doc-nav-items ${collapsedGroups[section.group] ? 'collapsed' : ''}`}>
                {section.items.map(item => (
                  <button
                    key={item.id}
                    className={`doc-nav-link ${activeSection === item.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(item.id)}
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="doc-sidebar-version">v1.0.0</div>
      </div>

      {/* ═══════════ MAIN CONTENT ═══════════ */}
      <div className="doc-main" ref={mainContentRef}>
        <div className="doc-main-inner">

          {/* ── Search Bar ── */}
          <div className="doc-search-wrapper">
            <div className="doc-search-container" ref={searchInputRef}>
              <span className="doc-search-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                </svg>
              </span>
              <input
                className="doc-search-input"
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => { if (searchResults.length > 0) setShowSearchDropdown(true); }}
              />
              {showSearchDropdown && searchResults.length > 0 && (
                <div className="doc-search-dropdown">
                  {searchResults.map(r => (
                    <div
                      key={r.id}
                      className="doc-search-result"
                      onClick={() => scrollToSection(r.id)}
                    >
                      <div className="doc-search-result-title">
                        {highlightMatch(r.title, searchQuery)}
                      </div>
                      <div className="doc-search-result-breadcrumb">
                        {r.group} &rsaquo; {r.title}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Ask Insta Button */}
            <button className="doc-ask-btn" onClick={() => setAskInstaOpen(true)}>
              <span className="doc-ask-iconwrap" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
                <svg className="doc-ask-sparkle" width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11 3 L13 8.5 L18.5 10.5 L13 12.5 L11 18 L9 12.5 L3.5 10.5 L9 8.5 Z" />
                </svg>
              </span>
              Ask Insta
            </button>
          </div>

          {/* ═══════════ GETTING STARTED ═══════════ */}

          {/* ── Product Overview ── */}
          <h2 className="doc-section-heading" id="overview">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
            </span>
            Product Overview
          </h2>
          <p className="doc-p">InstaGuide.ai is a documentation automation platform that turns everyday browser workflows into polished, step-by-step guides. Instead of manually writing instructions and taking screenshots, simply <strong>record your process</strong> and let AI do the rest.</p>

          <div className="doc-card-grid">
            <div className="doc-feature-card">
              <div className="doc-feature-card-top">
                <div className="doc-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                </div>
              </div>
              <div className="doc-feature-card-body">
                <h4>Auto-Capture</h4>
                <p>Automatically captures clicks, keyboard actions, and scrolls with pixel-perfect screenshots.</p>
              </div>
            </div>
            <div className="doc-feature-card">
              <div className="doc-feature-card-top">
                <div className="doc-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" /><path d="M8 12l3 3 5-5" /></svg>
                </div>
              </div>
              <div className="doc-feature-card-body">
                <h4>Auto Descriptions</h4>
                <p>InstaGuide analyzes each screenshot and generates clear, human-readable step instructions.</p>
              </div>
            </div>
            <div className="doc-feature-card">
              <div className="doc-feature-card-top">
                <div className="doc-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></svg>
                </div>
              </div>
              <div className="doc-feature-card-body">
                <h4>Instant Guides</h4>
                <p>Browse, annotate, and share guides through an elegant web dashboard.</p>
              </div>
            </div>
            <div className="doc-feature-card">
              <div className="doc-feature-card-top">
                <div className="doc-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>
                </div>
              </div>
              <div className="doc-feature-card-body">
                <h4>Team Collaboration</h4>
                <p>Organizations, roles, sharing, and comments for seamless team documentation.</p>
              </div>
            </div>
          </div>

          <h3 className="doc-h3" id="overview-how-it-works">How It Works</h3>
          <ol className="doc-steps">
            <li><strong>Install the Chrome Extension</strong> &mdash; Add InstaGuide to your browser in one click.</li>
            <li><strong>Start Recording</strong> &mdash; Click the extension icon and begin your workflow. Every click, keystroke, and scroll is captured with a screenshot.</li>
            <li><strong>InstaGuide Generates Descriptions</strong> &mdash; Each step is analyzed by InstaGuide, which writes a concise instruction based on what it sees in the screenshot.</li>
            <li><strong>Review on Dashboard</strong> &mdash; Open the web dashboard to view your guide, add annotations, reorder steps, and customize.</li>
            <li><strong>Share &amp; Export</strong> &mdash; Share with teammates, export as HTML or PDF, or organize into project folders.</li>
          </ol>

          {/* ── Key Features ── */}
          <h2 className="doc-section-heading" id="key-features">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" /></svg>
            </span>
            Key Features
          </h2>

          <div className="doc-card">
            <h4 className="doc-h4">Chrome Extension</h4>
            <ul className="doc-ul">
              <li className="doc-li">One-click recording of browser workflows</li>
              <li className="doc-li">Automatic screenshot capture on every interaction</li>
              <li className="doc-li">Smart event filtering to reduce noise and capture only meaningful interactions</li>
              <li className="doc-li">Voice narration support with real-time transcription</li>
              <li className="doc-li">Floating control panel that stays out of your way</li>
              <li className="doc-li">Side panel for live step preview during recording</li>
              <li className="doc-li">Insert mode to add new steps into existing guides</li>
            </ul>
          </div>

          <div className="doc-card">
            <h4 className="doc-h4">Web Dashboard</h4>
            <ul className="doc-ul">
              <li className="doc-li">Guide grid with search, favorites, and recent guides</li>
              <li className="doc-li">Step-by-step detail view with click indicators</li>
              <li className="doc-li">Screenshot annotation tools (circles, rectangles, arrows, text, blur)</li>
              <li className="doc-li">Interactive workflow flowchart with domain-based grouping</li>
              <li className="doc-li">Project folders for organizing guides</li>
              <li className="doc-li">Team sharing with view/edit permissions</li>
              <li className="doc-li">Guide comments for collaboration</li>
              <li className="doc-li">HTML and PDF export with full formatting</li>
              <li className="doc-li">6 color themes to personalize your workspace</li>
              <li className="doc-li">Organization management with custom roles</li>
              <li className="doc-li">Flexible AI provider support for step description generation</li>
            </ul>
          </div>

          <div className="doc-card">
            <h4 className="doc-h4">Meeting Mode</h4>
            <ul className="doc-ul">
              <li className="doc-li"><strong>Live path</strong> — record from the extension during a meeting; voice narration on by default, clicks captured in real time</li>
              <li className="doc-li"><strong>Import path</strong> — upload a saved video (Zoom, Teams, Google Meet, Loom) after the meeting and get a full guide back</li>
              <li className="doc-li">Optional VTT / SRT transcript for ground-truth speaker attribution on the import path</li>
              <li className="doc-li">Optional segment review — pick which topics become guides, combine, or split into multiple guides</li>
              <li className="doc-li">Meeting Highlights + Participants blocks generated automatically</li>
              <li className="doc-li">Direct-to-cloud upload with progress indicator — no gateway body-size limits</li>
              <li className="doc-li">Recordings auto-deleted after processing — no long-term video storage</li>
            </ul>
          </div>

          {/* ── Quick Start ── */}
          <h2 className="doc-section-heading" id="quick-start">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
            </span>
            Quick Start
          </h2>
          <p className="doc-p">Get up and running in under 5 minutes:</p>
          <ol className="doc-steps">
            <li><strong>Create an Account</strong> &mdash; Visit the InstaGuide dashboard and sign up with your email. Choose between a personal or organization account.</li>
            <li><strong>Install the Extension</strong> &mdash; Install InstaGuide from the Chrome Web Store.</li>
            <li><strong>Log In to the Extension</strong> &mdash; Click the InstaGuide icon in your browser toolbar and enter your credentials.</li>
            <li><strong>Record Your First Guide</strong> &mdash; Click <strong>&ldquo;Start Recording&rdquo;</strong>, enter a guide name, and perform your workflow. Click <strong>&ldquo;Stop&rdquo;</strong> when done.</li>
            <li><strong>View Your Guide</strong> &mdash; Open the dashboard to see your guide with descriptions written by InstaGuide for every step.</li>
          </ol>

          {/* ═══════════ CHROME EXTENSION ═══════════ */}

          {/* ── Installation ── */}
          <h2 className="doc-section-heading" id="ext-install">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /></svg>
            </span>
            Chrome Extension &mdash; Installation
          </h2>

          <h3 className="doc-h3" id="ext-install-chrome-web-store">From the Chrome Web Store</h3>
          <ol className="doc-steps">
            <li>Open Google Chrome and visit the InstaGuide listing on the Chrome Web Store.</li>
            <li>Click <strong>&ldquo;Add to Chrome&rdquo;</strong> and confirm the installation.</li>
            <li>The InstaGuide icon will appear in your browser toolbar. Pin it for easy access.</li>
          </ol>

          <div className="doc-callout info">
            <strong>Tip</strong> If the extension icon doesn't appear, click the puzzle piece icon in Chrome's toolbar and pin InstaGuide for quick access.
          </div>

          {/* ── Login ── */}
          <h2 className="doc-section-heading" id="ext-login">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3" /></svg>
            </span>
            Chrome Extension &mdash; Login
          </h2>
          <p className="doc-p">Before recording, you must authenticate with your InstaGuide account:</p>
          <ol className="doc-steps">
            <li>Click the InstaGuide icon in the toolbar to open the popup.</li>
            <li>If not logged in, you'll see the login page. Enter your email and password.</li>
            <li>On success, the popup switches to the recording controls.</li>
          </ol>
          <p className="doc-p">Your login session is stored securely and automatically refreshes every hour while you&apos;re active. After 60 minutes of inactivity, you&apos;ll be logged out. Extension sessions remain valid for 30 days.</p>

          {/* ── Recording a Guide ── */}
          <h2 className="doc-section-heading" id="ext-recording">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" fill="#fff" /></svg>
            </span>
            Recording a Guide
          </h2>
          <ol className="doc-steps">
            <li>Click the InstaGuide extension icon to open the popup.</li>
            <li>Click <strong>&ldquo;Start Recording&rdquo;</strong>. A dialog appears with two options: <strong>Self Mode</strong> (default) and <strong>Meeting Mode</strong>. Pick one, enter a name, and hit Start.</li>
            <li>Perform your workflow normally. Every click, keyboard shortcut, and significant scroll is captured with a screenshot.</li>
            <li>When finished, click the <strong>Stop</strong> button on the floating panel, or open the popup and click <strong>&ldquo;Stop Recording&rdquo;</strong>.</li>
            <li>A confirmation dialog appears with an option to <strong>&ldquo;View Guide&rdquo;</strong> &mdash; this opens the dashboard to your new guide.</li>
          </ol>

          <div className="doc-callout success">
            <strong>What gets captured</strong> InstaGuide captures a full-page screenshot at the moment of each interaction, along with the click location and page context.
          </div>

          {/* ── Recording Modes ── */}
          <h2 className="doc-section-heading" id="ext-modes">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3" y="4" width="8" height="16" rx="2"/><rect x="13" y="4" width="8" height="16" rx="2"/></svg>
            </span>
            Recording Modes
          </h2>
          <p className="doc-p">
            When you click <strong>Start Recording</strong>, the extension asks how you&apos;re capturing: <strong>Self Mode</strong> or <strong>Meeting Mode</strong>. The mode you pick tunes a few defaults; you can still switch voice and other controls on the floating panel mid-recording.
          </p>

          <div className="doc-card">
            <h4 className="doc-h4">Self Mode <span style={{ fontWeight: 400, color: '#64748b', fontSize: 13 }}>(default)</span></h4>
            <p className="doc-p">For capturing <strong>your own workflow</strong> &mdash; a task you&apos;re walking through yourself. Voice narration is <strong>off</strong> by default (turn it on from the floater if you want to narrate). The name prompt asks <em>&ldquo;Guide name&rdquo;</em>. This is the classic InstaGuide capture flow.</p>
          </div>

          <div className="doc-card">
            <h4 className="doc-h4">Meeting Mode</h4>
            <p className="doc-p">For capturing <strong>during a live meeting</strong> where you&apos;re presenting or being talked through a workflow. Voice narration is <strong>on</strong> by default so the audio of the conversation becomes the step descriptions. The name prompt asks <em>&ldquo;What are you demoing?&rdquo;</em>. Before recording starts, a one-time privacy notice reminds you that screenshots capture everything visible on your screen &mdash; including attendee thumbnails and chat panels.</p>
            <p className="doc-p"><strong>Tip:</strong> share the specific window of the app you&apos;re demoing (not your whole screen), or hide the meeting app&apos;s chat and participants panel before clicking Start. Meeting Mode is available on Pro and above.</p>
          </div>

          <div className="doc-callout info">
            Meeting Mode also has a second path &mdash; <strong>uploading a saved recording</strong> after the meeting instead of capturing live. That flow lives in the Web Dashboard, not the extension. See <a href="#import-overview">Import Recording</a> for the upload path.
          </div>

          {/* ── Floating Panel ── */}
          <h2 className="doc-section-heading" id="ext-floating-panel">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
            </span>
            Floating Panel
          </h2>
          <p className="doc-p">During recording, a compact floating panel appears in the corner of your browser window. It provides quick access to recording controls without leaving your workflow.</p>

          <div className="doc-card">
            <h4 className="doc-h4">Floating Panel Controls</h4>
            <table className="doc-table">
              <thead><tr><th>Control</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><strong>Voice Toggle</strong></td><td>Enable/disable voice narration recording</td></tr>
                <tr><td><strong>Preview</strong></td><td>Opens the Chrome Side Panel to preview captured steps</td></tr>
                <tr><td><strong>Capture</strong></td><td>Manually capture a screenshot without a click event</td></tr>
                <tr><td><strong>Pause / Resume</strong></td><td>Suspend capture without ending the session. Clicks, keys, scrolls, and voice all stop until you click Resume. The status indicator turns amber while paused. Also available in the Side Panel.</td></tr>
                <tr><td><strong>Settings</strong></td><td>Opens Capture Settings to configure which events are recorded — and view or change your organization's redaction settings inline</td></tr>
                <tr><td><strong>Stop</strong></td><td>Stops the recording session</td></tr>
                <tr><td><strong>Minimize/Expand</strong></td><td>Collapse the panel to a small right-edge tab, or expand it back</td></tr>
              </tbody>
            </table>
          </div>
          <p className="doc-p">The floating panel can be dragged to reposition it anywhere on the page. It automatically minimizes when you open the Side Panel preview.</p>

          {/* ── Side Panel Preview ── */}
          <h2 className="doc-section-heading" id="ext-side-panel">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /></svg>
            </span>
            Side Panel Preview
          </h2>
          <p className="doc-p">The Chrome Side Panel provides a live preview of your captured steps during recording:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Thumbnail previews</strong> &mdash; Optimized thumbnails load instantly</li>
            <li className="doc-li"><strong>Step list</strong> &mdash; See all captured steps in order with descriptions written by InstaGuide</li>
            <li className="doc-li"><strong>Delete steps</strong> &mdash; Remove unwanted steps directly from the side panel</li>
            <li className="doc-li"><strong>Real-time sync</strong> &mdash; New steps appear automatically as you interact</li>
          </ul>
          <p className="doc-p">Click the <strong>Preview</strong> button (eye icon) on the floating panel to open the Side Panel.</p>

          {/* ── Voice Narration ── */}
          <h2 className="doc-section-heading" id="ext-voice">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" /></svg>
            </span>
            Voice Narration
          </h2>
          <p className="doc-p">Add voice narration to your guides for richer context:</p>
          <ol className="doc-steps">
            <li>During recording, click the <strong>Voice</strong> toggle on the floating panel.</li>
            <li>Speak naturally as you perform each step. Your audio is continuously captured.</li>
            <li>When each step is captured, the recorded audio segment is automatically transcribed to text.</li>
            <li>Transcriptions appear as <strong>voice transcripts</strong> alongside each step in the dashboard.</li>
          </ol>

          <div className="doc-callout warning">
            <strong>Microphone permission</strong> &mdash; on first install, the extension opens a brief onboarding tab that walks you through enabling microphone access. When Chrome's permission prompt appears, choose <strong>"Allow visiting this site"</strong> (the persistent option). <strong>"Allow this time"</strong> won't work for voice recording &mdash; the session-only grant doesn't carry into the background audio capture document, so transcription will fail silently.
          </div>

          {/* ── Inserting Steps ── */}
          <h2 className="doc-section-heading" id="ext-insert">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 8v8M8 12h8" /></svg>
            </span>
            Inserting Steps
          </h2>
          <p className="doc-p">Need to add a missing step to a completed guide? Use <strong>Insert Mode</strong>:</p>
          <ol className="doc-steps">
            <li>Open the guide in the dashboard and locate where you want to insert a step.</li>
            <li>Click the <strong>&ldquo;+&rdquo;</strong> button that appears between steps.</li>
            <li>The Chrome Extension enters Insert Mode &mdash; a special recording session that inserts new steps at the specified position.</li>
            <li>Perform the actions you want to capture. Each step is inserted at the correct position.</li>
            <li>Stop the recording. Steps are automatically renumbered.</li>
          </ol>

          {/* ── Captured Events ── */}
          <h2 className="doc-section-heading" id="ext-events">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
            </span>
            Captured Events
          </h2>
          <p className="doc-p">InstaGuide captures three types of browser events:</p>

          <div className="doc-card-grid" id="ext-events-click">
            <div className="doc-card" style={{ borderTop: '3px solid #7c3aed' }}>
              <h4 className="doc-h4"><span className="doc-event-badge click">Click Events</span></h4>
              <p className="doc-p">Every mouse click is captured with:</p>
              <ul className="doc-ul">
                <li className="doc-li">Exact click position on the page</li>
                <li className="doc-li">Target element identification</li>
                <li className="doc-li">High-resolution screenshot</li>
                <li className="doc-li">Page URL and context</li>
              </ul>
              <p style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic', margin: 0 }}>Smart duplicate prevention ensures only intentional clicks are captured.</p>
            </div>
            <div className="doc-card" style={{ borderTop: '3px solid #d97706' }} id="ext-events-keyboard">
              <h4 className="doc-h4"><span className="doc-event-badge keyboard">Keyboard Events</span></h4>
              <p className="doc-p">Captures important keyboard actions:</p>
              <ul className="doc-ul">
                <li className="doc-li"><span className="doc-kbd">Enter</span> and <span className="doc-kbd">Escape</span> keys</li>
                <li className="doc-li">Common shortcuts: <span className="doc-kbd">Ctrl+S</span>, <span className="doc-kbd">Ctrl+Z</span>, <span className="doc-kbd">Ctrl+F</span>, <span className="doc-kbd">Ctrl+P</span>, <span className="doc-kbd">Ctrl+A</span></li>
                <li className="doc-li">Key combination and context</li>
              </ul>
              <p style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic', margin: 0 }}>Regular typing is not captured to reduce noise and protect sensitive input.</p>
            </div>
            <div className="doc-card" style={{ borderTop: '3px solid #059669' }} id="ext-events-scroll">
              <h4 className="doc-h4"><span className="doc-event-badge scroll">Scroll Events</span></h4>
              <p className="doc-p">Significant page scrolls are tracked:</p>
              <ul className="doc-ul">
                <li className="doc-li">Scroll direction and distance</li>
                <li className="doc-li">Intelligent filtering to capture only meaningful scrolls</li>
                <li className="doc-li">Limited per session to avoid clutter</li>
                <li className="doc-li">Major page section changes are detected</li>
              </ul>
              <p style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic', margin: 0 }}>Only significant navigation is captured to keep guides clean and focused.</p>
            </div>
          </div>

          {/* ── Capture Settings ── */}
          <h2 className="doc-section-heading" id="ext-capture-settings">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
            </span>
            Capture Settings
          </h2>
          <p className="doc-p">Fine-tune what gets recorded using the Settings gear icon on the floating panel (available before and during recording) or from the extension popup.</p>

          <div className="doc-card">
            <h4 className="doc-h4">Event Types</h4>
            <table className="doc-table">
              <thead><tr><th>Setting</th><th>Default</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><strong>Right Click</strong></td><td>Off</td><td>Capture right-click context menu actions</td></tr>
                <tr><td><strong>Double Click</strong></td><td>Off</td><td>Capture double-click interactions</td></tr>
                <tr><td><strong>Keyboard Shortcuts</strong></td><td>On</td><td>Ctrl+S, Ctrl+Z, and other important shortcuts</td></tr>
                <tr><td><strong>Enter &amp; Escape</strong></td><td>On</td><td>Form submissions and dialog closes</td></tr>
                <tr><td><strong>Tab Key</strong></td><td>Off</td><td>Tab navigation between fields</td></tr>
                <tr><td><strong>Arrow Keys</strong></td><td>Off</td><td>Directional navigation</td></tr>
                <tr><td><strong>Backspace / Delete</strong></td><td>Off</td><td>Text deletion actions</td></tr>
                <tr><td><strong>All Typed Text</strong></td><td>Off</td><td>Captures text typed into form fields (password fields excluded for security)</td></tr>
                <tr><td><strong>Page Scroll</strong></td><td>Off</td><td>Significant page scroll events</td></tr>
                <tr><td><strong>Page Navigation</strong></td><td>Off</td><td>URL change detection</td></tr>
              </tbody>
            </table>
          </div>

          <div className="doc-card">
            <h4 className="doc-h4">Sensitivity</h4>
            <table className="doc-table">
              <thead><tr><th>Setting</th><th>Options</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><strong>Click Debounce</strong></td><td>Low / Normal / High</td><td>Controls how quickly consecutive clicks are deduplicated. Higher = fewer captures.</td></tr>
                <tr><td><strong>Scroll Threshold</strong></td><td>Low / Medium / High</td><td>Minimum scroll distance before capture. Higher = only large scrolls recorded.</td></tr>
              </tbody>
            </table>
          </div>

          <div className="doc-callout info">
            <strong>Settings are live</strong> Changes apply immediately, even during an active recording session. Settings persist across sessions.
          </div>

          {/* ═══════════ DESKTOP APP ═══════════ */}

          {/* ── Desktop Overview & Installation ── */}
          <h2 className="doc-section-heading" id="desktop-overview">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
            </span>
            Desktop App &mdash; Overview &amp; Installation
          </h2>
          <p className="doc-p">The InstaGuide Desktop App captures interactions across <strong>any desktop application</strong> &mdash; not just browsers. Capture workflows in Photoshop, Excel, VS Code, or any other app.</p>

          <h3 className="doc-h3" id="desktop-overview-features">Key Features</h3>
          <div className="doc-card-grid">
            <div className="doc-feature-card">
              <div className="doc-feature-card-top">
                <div className="doc-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
                </div>
              </div>
              <div className="doc-feature-card-body">
                <h4>Global Capture</h4>
                <p>Tracks clicks and keystrokes across all applications using system-level hooks.</p>
              </div>
            </div>
            <div className="doc-feature-card">
              <div className="doc-feature-card-top">
                <div className="doc-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </div>
              </div>
              <div className="doc-feature-card-body">
                <h4>Offline-First</h4>
                <p>Works without internet. Data is stored locally and synced when connected.</p>
              </div>
            </div>
            <div className="doc-feature-card">
              <div className="doc-feature-card-top">
                <div className="doc-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                </div>
              </div>
              <div className="doc-feature-card-body">
                <h4>Multi-Display</h4>
                <p>Select specific screens or windows to capture with a visual target selector.</p>
              </div>
            </div>
          </div>

          <h3 className="doc-h3" id="desktop-overview-install">Installation</h3>
          <ol className="doc-steps">
            <li>Download the installer for your platform (Windows or macOS) from the InstaGuide website.</li>
            <li>Run the installer and follow the on-screen instructions.</li>
            <li>Launch InstaGuide Desktop and log in with your account credentials.</li>
          </ol>

          {/* ── Desktop Recording Flow ── */}
          <h2 className="doc-section-heading" id="desktop-recording">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3" fill="#fff"/></svg>
            </span>
            Desktop App &mdash; Recording Flow
          </h2>

          <h3 className="doc-h3" id="desktop-recording-target">Target Selection</h3>
          <p className="doc-p">Before recording starts, you choose what to capture:</p>
          <ol className="doc-steps">
            <li>Click <strong>&ldquo;Start Recording&rdquo;</strong> and enter a guide name.</li>
            <li>The Target Selection dialog shows all available screens and windows with thumbnails.</li>
            <li>Select your target &mdash; a green highlight briefly appears to confirm the selection.</li>
            <li>Recording begins and the main window hides. The target application is focused automatically.</li>
            <li>A floating control bar appears with capture, pause, and stop buttons.</li>
          </ol>

          <h3 className="doc-h3" id="desktop-recording-modes">Recording Modes</h3>
          <div className="doc-card">
            <table className="doc-table">
              <thead><tr><th>Mode</th><th>What It Captures</th></tr></thead>
              <tbody>
                <tr><td><strong>Full Desktop</strong></td><td>All screens and windows &mdash; captures everything</td></tr>
                <tr><td><strong>Screen</strong></td><td>A specific display only &mdash; filters clicks by screen bounds</td></tr>
                <tr><td><strong>Window</strong></td><td>A specific application window only &mdash; filters by window bounds</td></tr>
              </tbody>
            </table>
          </div>

          {/* ── Desktop Offline & Sync ── */}
          <h2 className="doc-section-heading" id="desktop-sync">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
            </span>
            Desktop App &mdash; Offline &amp; Sync
          </h2>
          <p className="doc-p">The Desktop App is <strong>offline-first</strong>. All guides and steps are stored locally on your computer. When connected to the internet:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Auto-sync</strong> runs every 60 seconds (configurable in Settings)</li>
            <li className="doc-li"><strong>Manual sync</strong> can be triggered from the user menu</li>
            <li className="doc-li">Steps are uploaded and AI descriptions are generated automatically</li>
            <li className="doc-li">Synced guides appear in the web dashboard with a <strong>Desktop</strong> source indicator</li>
          </ul>

          <div className="doc-callout success">
            <strong>AI Provider</strong> The Desktop App inherits the organization's AI provider settings. No desktop-side AI configuration is needed &mdash; just configure it once in Organization Settings.
          </div>

          {/* ═══════════ MEETING MODE ═══════════ */}

          <h2 className="doc-section-heading" id="meeting-overview">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
            </span>
            Meeting Mode &mdash; Overview
          </h2>
          <p className="doc-p">
            <strong>Meeting Mode</strong> turns a meeting into a fully-structured InstaGuide &mdash; screenshots, step descriptions, participants, and a Meeting Highlights summary &mdash; without anyone on the call having to record their clicks. Two ways to use it:
          </p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Live capture during a meeting</strong> &mdash; use the Chrome extension&apos;s Meeting Mode option when you click Start Recording. Voice narration is on by default; the guide is ready seconds after you click Stop. Full details in <a href="#ext-modes">Chrome Extension &rarr; Recording Modes</a>.</li>
            <li className="doc-li"><strong>Import a saved recording</strong> &mdash; upload the video after the meeting from the dashboard. See <a href="#import-overview">Import Recording</a> below for the upload flow.</li>
          </ul>
          <p className="doc-p">Both paths produce the same output: a shareable, editable, exportable InstaGuide with the meeting-specific extras below.</p>

          <h2 className="doc-section-heading" id="meeting-output">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            </span>
            What Gets Generated
          </h2>
          <p className="doc-p">A meeting-generated guide includes everything a manually-recorded guide does, plus meeting-specific context:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Ordered steps with screenshots</strong> &mdash; frames extracted at moments Insta identifies as workflow-relevant (a click, a form submission, a state change)</li>
            <li className="doc-li"><strong>Step descriptions</strong> &mdash; short, imperative sentences describing what happens at each frame</li>
            <li className="doc-li"><strong>Meeting Highlights</strong> &mdash; a block on the guide detail page (and in exports) summarizing key facts, outcomes, and decisions from the recording</li>
            <li className="doc-li"><strong>Participants</strong> &mdash; who spoke, their inferred role, and what they contributed. Grounded in the transcript when supplied</li>
            <li className="doc-li"><strong>Workflow flowchart</strong> &mdash; the standard app-based flowchart with domain grouping, generated from the extracted steps</li>
          </ul>
          <p className="doc-p">
            The resulting guide is editable, shareable, embeddable, and exportable exactly like a manually-recorded guide. Screenshots can be re-annotated, steps can be reordered or deleted, and descriptions can be edited or regenerated.
          </p>

          <div className="doc-callout warning">
            <strong>Videos are not stored long-term.</strong> Uploaded recordings live in cloud storage only during processing. Once the generated guide is saved, the source video is deleted. Anything left behind by a failed processing run is automatically purged within a few days. Your recordings never become permanent InstaGuide storage.
          </div>

          <h2 className="doc-section-heading" id="meeting-workflows">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            </span>
            Sample Workflows
          </h2>
          <p className="doc-p">Four common ways teams use Meeting Mode. Pick the pattern closest to your situation.</p>

          <div className="doc-card">
            <h4 className="doc-h4">1. Sales demo &rarr; shareable how-to (Live)</h4>
            <p className="doc-p"><strong>Situation:</strong> You're walking a prospect through your product on a live call and want the demo itself to become a follow-up asset you can email them afterward.</p>
            <ul className="doc-ul">
              <li className="doc-li">Chrome extension &rarr; Start Recording &rarr; <strong>Meeting Mode</strong> &rarr; name it <em>&ldquo;{'{Prospect name}'} product tour&rdquo;</em>.</li>
              <li className="doc-li">Share the specific browser window (not full screen) so attendee thumbnails don't end up in the screenshots.</li>
              <li className="doc-li">Narrate as you click. The guide is ready in the dashboard when the call ends.</li>
              <li className="doc-li">Export as PDF or share the embed link in your follow-up email.</li>
            </ul>
          </div>

          <div className="doc-card">
            <h4 className="doc-h4">2. Customer onboarding kickoff &rarr; multi-guide training pack (Import + Review)</h4>
            <p className="doc-p"><strong>Situation:</strong> You just finished a two-hour onboarding call that covered four separate workflows. You want each workflow as its own guide so new hires can jump straight to what they need.</p>
            <ul className="doc-ul">
              <li className="doc-li">Download the meeting recording + the auto-generated transcript from your meeting tool.</li>
              <li className="doc-li">Dashboard &rarr; Imports &rarr; <strong>Import recording</strong>. Attach both files. Tick <strong>&ldquo;Review segments before creating guide&rdquo;</strong>.</li>
              <li className="doc-li">When analysis finishes, the segment review view lists the four topics Insta detected. Verify the topic boundaries, then choose <strong>&ldquo;Split into separate guides&rdquo;</strong>.</li>
              <li className="doc-li">Four guides land in your dashboard. Move them into an <em>&ldquo;Onboarding&rdquo;</em> project folder.</li>
            </ul>
          </div>

          <div className="doc-card">
            <h4 className="doc-h4">3. Recorded SME session &rarr; single tidy guide (Import + Review + Combine)</h4>
            <p className="doc-p"><strong>Situation:</strong> A subject-matter expert recorded a 45-minute walkthrough that included 5 minutes of small talk, 5 minutes of an unrelated tangent, and 35 minutes of the actual workflow spread across three related topics.</p>
            <ul className="doc-ul">
              <li className="doc-li">Dashboard &rarr; Imports &rarr; <strong>Import recording</strong>. Upload the video (+ transcript if you have one). Tick <strong>&ldquo;Review segments before creating guide&rdquo;</strong>.</li>
              <li className="doc-li">In the segment review, <strong>uncheck</strong> the small talk and the tangent. Leave the three workflow topics checked.</li>
              <li className="doc-li">Choose <strong>&ldquo;Combine into one guide&rdquo;</strong>. One clean guide is generated from the meaningful parts of the recording.</li>
            </ul>
          </div>

          <div className="doc-card">
            <h4 className="doc-h4">4. Loom / Zoom cloud recording &rarr; instant guide (Import, no review)</h4>
            <p className="doc-p"><strong>Situation:</strong> You already have a focused 10-minute Loom of yourself demoing one workflow. You want the guide ASAP with no manual review step.</p>
            <ul className="doc-ul">
              <li className="doc-li">Dashboard &rarr; Imports &rarr; <strong>Import recording</strong>. Attach the video. Leave the segment-review checkbox <strong>unchecked</strong>.</li>
              <li className="doc-li">Click <strong>Start processing</strong>. Insta runs end-to-end and produces one guide from the whole recording.</li>
              <li className="doc-li">Ideal for short, single-topic recordings where you don't need to curate.</li>
            </ul>
          </div>

          <h2 className="doc-section-heading" id="meeting-plans">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
            </span>
            Plan Availability
          </h2>
          <p className="doc-p">Meeting Mode is a paid feature. Monthly upload quotas vary by plan:</p>
          <table className="doc-table">
            <thead>
              <tr><th>Plan</th><th>Meeting Mode</th><th>Monthly uploads</th></tr>
            </thead>
            <tbody>
              <tr><td>Free</td><td>&mdash;</td><td>Not included</td></tr>
              <tr><td>Starter</td><td>&mdash;</td><td>Not included</td></tr>
              <tr><td><strong>Pro</strong></td><td>Included</td><td>10 / month</td></tr>
              <tr><td><strong>Business</strong></td><td>Included</td><td>50 / month</td></tr>
              <tr><td><strong>Enterprise</strong></td><td>Included</td><td>Configurable (unlimited by default)</td></tr>
            </tbody>
          </table>
          <p className="doc-p">
            Quotas reset on the first of each month. Reaching the cap surfaces a friendly upgrade prompt at upload time; guides already generated remain fully editable and shareable.
          </p>

          {/* ═══════════ IMPORT RECORDING ═══════════ */}

          <h2 className="doc-section-heading" id="import-overview">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            </span>
            Import Recording &mdash; Overview
          </h2>
          <p className="doc-p">
            <strong>Import Recording</strong> is the dashboard-side of Meeting Mode. The meeting already happened and you have a saved video (Zoom cloud recording, Teams recording, Google Meet export, Loom, Riverside &mdash; any MP4/MOV/WebM/MKV). Upload the file and InstaGuide turns it into a structured guide.
          </p>
          <p className="doc-p">
            Import Recording is available on Pro, Business, and Enterprise. Monthly upload counts follow the Meeting Mode quota above.
          </p>

          <h2 className="doc-section-heading" id="import-upload">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            </span>
            Uploading a Recording
          </h2>
          <p className="doc-p">
            From the dashboard sidebar, open <strong>Imports</strong> and click <strong>&ldquo;Import recording&rdquo;</strong>. In the modal:
          </p>
          <ol className="doc-ol">
            <li className="doc-li"><strong>Video file</strong> &mdash; drag and drop, or click to select. Supported formats: MP4, MOV, WebM, MKV. Maximum size: <strong>1 GB per upload</strong>. Maximum duration: <strong>60 minutes</strong> (or 2 hours if you also attach a transcript &mdash; see below).</li>
            <li className="doc-li"><strong>Transcript file</strong> (optional) &mdash; the <code>.vtt</code> or <code>.srt</code> exported by your meeting tool. See the next section for why this helps.</li>
            <li className="doc-li"><strong>Meeting topic</strong> (optional) &mdash; a one-line hint that helps focus the analysis (e.g., <em>&ldquo;Customer walkthrough &mdash; invoicing workflow&rdquo;</em>).</li>
            <li className="doc-li"><strong>App or domain hint</strong> (optional) &mdash; the name of the tool being demoed (e.g., <em>&ldquo;Salesforce&rdquo;</em>, <em>&ldquo;Excel&rdquo;</em>). Useful when the audio doesn&apos;t make it obvious.</li>
            <li className="doc-li"><strong>Review segments</strong> (optional checkbox) &mdash; opt into the segment-review workflow before any guides are built. See <a href="#import-review">Segment Review</a> below.</li>
            <li className="doc-li"><strong>Consent checkbox</strong> &mdash; confirm you have permission to upload the recording. Required to submit.</li>
            <li className="doc-li">Click <strong>&ldquo;Start processing&rdquo;</strong>. A progress bar shows real-time upload percentage.</li>
          </ol>

          <p className="doc-p">
            After the upload completes, close the modal and go to the <strong>Imports</strong> page to watch processing progress. A typical one-hour recording takes 10&ndash;20 minutes.
          </p>

          <h2 className="doc-section-heading" id="import-transcript">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            </span>
            Optional Transcript
          </h2>
          <p className="doc-p">
            You can optionally attach a <strong>transcript file</strong> (<code>.vtt</code> or <code>.srt</code>) alongside the video. Zoom, Teams, Google Meet, and Loom all export these. When provided:
          </p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Real speaker names</strong> &mdash; the guide&apos;s Participants block uses the names from the transcript instead of generic labels.</li>
            <li className="doc-li"><strong>More accurate segmentation</strong> &mdash; segment boundaries and topic labels reflect what was actually said.</li>
            <li className="doc-li"><strong>Faster processing</strong> &mdash; guides finish sooner when the transcript is supplied.</li>
          </ul>
          <p className="doc-p">
            If no transcript is provided, guides are still generated &mdash; speakers are labeled as <em>&ldquo;Speaker 1&rdquo;</em> / <em>&ldquo;Speaker 2&rdquo;</em>.
          </p>

          <h2 className="doc-section-heading" id="import-review">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
            </span>
            Segment Review (Optional)
          </h2>
          <p className="doc-p">
            When uploading, check the <strong>&ldquo;Review segments before creating guide&rdquo;</strong> box to opt into the segment review workflow. InstaGuide will analyze the meeting, identify distinct topics, and then <strong>pause and wait for you</strong> before assembling any guides.
          </p>
          <p className="doc-p">In the review view you can:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Select which topics to include</strong> &mdash; skip parts that don&apos;t belong in the final guide (small talk, off-topic detours).</li>
            <li className="doc-li"><strong>Combine multiple topics into one guide</strong> &mdash; useful when a single workflow spans several segments.</li>
            <li className="doc-li"><strong>Split into separate guides</strong> &mdash; if the meeting covered several distinct workflows, produce one guide per topic in a single processing run.</li>
            <li className="doc-li"><strong>Cancel</strong> &mdash; discard the whole upload if the analysis suggests it isn&apos;t guide-worthy.</li>
          </ul>
          <p className="doc-p">
            When the review box is unchecked (default), Insta runs end-to-end and produces one guide from the whole recording &mdash; the low-friction path for straightforward demos.
          </p>

          {/* ═══════════ WEB DASHBOARD ═══════════ */}

          {/* ── Login & Signup ── */}
          <h2 className="doc-section-heading" id="dash-login">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
            </span>
            Dashboard &mdash; Login &amp; Signup
          </h2>

          <h3 className="doc-h3" id="dash-login-creating">Creating an Account</h3>
          <p className="doc-p">Visit the InstaGuide dashboard and click <strong>&ldquo;Sign Up&rdquo;</strong>. You have two account types:</p>
          <table className="doc-table">
            <thead><tr><th>Account Type</th><th>Description</th><th>Best For</th></tr></thead>
            <tbody>
              <tr><td><strong>Personal</strong></td><td>Individual account with private guides and personal projects</td><td>Solo users, freelancers</td></tr>
              <tr><td><strong>Organization</strong></td><td>Team workspace with member management, roles, shared guides, and centralized AI settings</td><td>Teams, companies</td></tr>
            </tbody>
          </table>

          <div className="doc-callout warning">
            <strong>Account Approval</strong> New accounts require administrator approval before you can log in. After signing up, you will see a confirmation message. Once the administrator approves your account, you can log in with your credentials.
          </div>

          <h3 className="doc-h3" id="dash-login-logging-in">Logging In</h3>
          <p className="doc-p">Enter your email and password. On success, you&apos;re securely signed in and taken to the dashboard home.</p>

          <div className="doc-callout info">
            <strong>Invitation Flow</strong> If you received a team invitation email, click the link to sign up with the organization pre-filled. Your email will be pre-populated from the invitation. Invitation members are auto-approved (no admin approval needed).
          </div>

          {/* ── Dashboard Home ── */}
          <h2 className="doc-section-heading" id="dash-home">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
            </span>
            Dashboard Home
          </h2>
          <p className="doc-p">The dashboard home is your central hub for all guides. Navigate between pages using the left sidebar.</p>

          <div className="doc-card">
            <h4 className="doc-h4">Navigation Pages</h4>
            <ul className="doc-ul">
              <li className="doc-li"><strong>Home</strong> &mdash; Overview with recent guides, favorites, and quick stats</li>
              <li className="doc-li"><strong>My Guides</strong> &mdash; All guides you&apos;ve created with full search and filtering</li>
              <li className="doc-li"><strong>Shared With Me</strong> &mdash; Guides shared with you by organization members</li>
              <li className="doc-li"><strong>Favorites</strong> &mdash; Your starred guides for quick access</li>
              <li className="doc-li"><strong>Projects</strong> &mdash; Browse and manage guides organized by project folders</li>
              <li className="doc-li"><strong>Profile</strong> &mdash; View your account info and change your password</li>
              <li className="doc-li"><strong>Administration</strong> &mdash; Organization settings (members, roles, projects, AI provider)</li>
            </ul>
          </div>

          <div className="doc-card">
            <h4 className="doc-h4">Guide Cards</h4>
            <ul className="doc-ul">
              <li className="doc-li"><strong>Guide Cards</strong> &mdash; Each guide shows its name, creation date, step count, and a thumbnail preview</li>
              <li className="doc-li"><strong>Source Indicator</strong> &mdash; Desktop icon (purple) or Chrome icon (amber) showing where the guide was created</li>
              <li className="doc-li"><strong>Owner Name</strong> &mdash; Creator name displayed on the guide card</li>
              <li className="doc-li"><strong>Favorite Star</strong> &mdash; Click the star to add a guide to your favorites (synced to your account)</li>
              <li className="doc-li"><strong>Recent Guides</strong> &mdash; Your 20 most recently viewed guides appear on the Home page</li>
            </ul>
          </div>

          <div className="doc-card">
            <h4 className="doc-h4">Search &amp; Filters</h4>
            <p className="doc-p">A unified filter bar appears on Home, My Guides, Shared With Me, Favorites, and Projects Explorer pages:</p>
            <ul className="doc-ul">
              <li className="doc-li"><strong>Search</strong> &mdash; Filter guides by name or domain in real time</li>
              <li className="doc-li"><strong>Filters Popover</strong> &mdash; Click the &ldquo;Filters&rdquo; button to open a popover panel with all filter options. A badge on the button shows the number of active filters</li>
              <li className="doc-li"><strong>Source Filter</strong> &mdash; Show only Chrome Extension or Desktop App guides</li>
              <li className="doc-li"><strong>Created By</strong> &mdash; Filter by guide creator (Everyone, Me, or specific team member)</li>
              <li className="doc-li"><strong>Project Filter</strong> &mdash; Show guides from a specific project</li>
              <li className="doc-li"><strong>Sort</strong> &mdash; Sort by newest, oldest, name A-Z, or most steps</li>
              <li className="doc-li"><strong>Filter Tags</strong> &mdash; Active filters appear as removable tags below the search bar. Click the &times; on any tag to remove that filter</li>
              <li className="doc-li"><strong>Clear All</strong> &mdash; Reset all active filters at once from the popover header</li>
              <li className="doc-li"><strong>Result Count</strong> &mdash; When filters are active, the number of matching guides is displayed</li>
              <li className="doc-li"><strong>View Mode Toggle</strong> &mdash; Switch between tile (grid) and list view. Your preference is saved across sessions</li>
              <li className="doc-li"><strong>Persistent Filters</strong> &mdash; Filter settings carry over when switching between pages</li>
            </ul>
          </div>

          {/* ── Guide Detail View ── */}
          <h2 className="doc-section-heading" id="dash-guide-detail">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
            </span>
            Guide Detail View
          </h2>
          <p className="doc-p">Click any guide card to open the detail view. This is where you review, edit, and enhance your guide.</p>

          <h3 className="doc-h3" id="dash-guide-detail-step-cards">Step Cards</h3>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Step Number</strong> &mdash; Sequential numbering, auto-maintained</li>
            <li className="doc-li"><strong>Screenshot</strong> &mdash; Full captured screenshot with click indicator overlay</li>
            <li className="doc-li"><strong>Click Indicator</strong> &mdash; A green &ldquo;CLICK HERE&rdquo; callout badge with arrow and pulsing dot, showing exactly where the click occurred</li>
            <li className="doc-li"><strong>Description</strong> &mdash; Auto-generated instruction text written by InstaGuide</li>
            <li className="doc-li"><strong>Event Type Badge</strong> &mdash; Color-coded: <span className="doc-event-badge click">click</span> <span className="doc-event-badge keyboard">keyboard</span> <span className="doc-event-badge scroll">scroll</span></li>
            <li className="doc-li"><strong>Voice Transcript</strong> &mdash; If voice narration was enabled, the transcription appears here</li>
          </ul>

          <h3 className="doc-h3" id="dash-guide-detail-step-actions">Step Actions</h3>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Edit Description</strong> &mdash; Click the description text to edit it inline</li>
            <li className="doc-li"><strong>Reorder Steps</strong> &mdash; Drag and drop steps to change their order</li>
            <li className="doc-li"><strong>Delete Step</strong> &mdash; Remove unwanted steps with confirmation</li>
            <li className="doc-li"><strong>Add Annotations</strong> &mdash; Open the annotation editor to mark up screenshots</li>
            <li className="doc-li"><strong>Insert Step (+)</strong> &mdash; Add new steps between existing ones</li>
            <li className="doc-li"><strong>Renumber Steps</strong> &mdash; Fix step numbering if it gets out of sequence</li>
            <li className="doc-li"><strong>Remove Duplicates</strong> &mdash; Automatically detect and remove duplicate steps</li>
          </ul>

          {/* ── Manual Step Types ── */}
          <h2 className="doc-section-heading" id="dash-manual-steps">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            </span>
            Manual Step Types
          </h2>
          <p className="doc-p">Click the <strong>+ Insert Step</strong> button between any two steps (or above the first step) to add content that isn't a captured click. The picker offers six step types &mdash; <em>capture a new click</em> via the extension, or insert one of five authored content types directly from the dashboard.</p>
          <div className="doc-callout info">
            Manual step types are perfect for adding context that doesn't come from clicking: introductions, warnings, reference images, voice notes, and chapter headings. They appear inline in the guide alongside captured steps and ship in HTML / PDF exports.
          </div>

          <h3 className="doc-h3" id="dash-manual-steps-rich-text">Rich Text</h3>
          <p className="doc-p">A free-form paragraph for narration, scripts, or context notes. Type or use the <strong>Dictate</strong> button to convert speech to text via the browser's built-in Web Speech API (no transcription cost). Use <strong>Enhance with Insta</strong> to polish what you've typed or dictated &mdash; the polish goes through your organization's configured AI Provider.</p>

          <h3 className="doc-h3" id="dash-manual-steps-callout">Callout</h3>
          <p className="doc-p">A highlighted notice with one of three severities:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Tip</strong> &mdash; blue, for helpful hints (e.g. <em>"Most users keep this on the default value."</em>)</li>
            <li className="doc-li"><strong>Warning</strong> &mdash; amber, for cautionary notes (e.g. <em>"Double-check the email before sending &mdash; recipients can't be edited afterwards."</em>)</li>
            <li className="doc-li"><strong>Critical</strong> &mdash; red, for must-not-skip information (e.g. <em>"Never share this API key publicly."</em>)</li>
          </ul>

          <h3 className="doc-h3" id="dash-manual-steps-image">Image</h3>
          <p className="doc-p">Upload a reference image (PNG, JPG, GIF, WebP) directly into the guide. Useful for diagrams, screenshots of external systems, or visual references that didn't come from the extension capture flow. Add an optional title and caption.</p>

          <h3 className="doc-h3" id="dash-manual-steps-audio">Audio Note</h3>
          <p className="doc-p">Add a voice clip that plays inline in the guide and in the HTML export. Two ways to provide the audio:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Record from mic</strong> &mdash; click <strong>Record</strong>, allow the browser microphone prompt, and click <strong>Stop</strong> when you're done. A live elapsed-time counter shows during recording.</li>
            <li className="doc-li"><strong>Choose audio file</strong> &mdash; upload any audio file (MP3, WAV, WebM, M4A) you recorded elsewhere.</li>
          </ul>
          <p className="doc-p">After saving, the audio is auto-transcribed via Azure Speech and the transcript is stored on the step. The audio player ships in HTML exports so anyone you send the file to can play it inline; in PDF exports the transcript replaces the player (since PDFs can't play audio) along with a "Audio note attached" indicator pointing readers to the HTML version.</p>
          <div className="doc-callout info">
            Mic recording requires Chrome, Edge, Firefox, or Safari 17+. The first time you click <strong>Record</strong> the browser will ask for microphone permission &mdash; choose <strong>Allow on every visit</strong> if you'll record often.
          </div>

          <h3 className="doc-h3" id="dash-manual-steps-section">Section Divider</h3>
          <p className="doc-p">A chapter heading that breaks long guides into logical sections (e.g. <em>"Setup"</em>, <em>"Daily Workflow"</em>, <em>"Troubleshooting"</em>). Renders as a larger title with optional subtitle and a horizontal rule, making it easy to scan a long procedure.</p>

          {/* ── Screenshot Annotations ── */}
          <h2 className="doc-section-heading" id="dash-annotations">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>
            </span>
            Screenshot Annotations
          </h2>
          <p className="doc-p">The built-in annotation editor lets you mark up screenshots to highlight important areas:</p>
          <table className="doc-table">
            <thead><tr><th>Tool</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Circle</strong></td><td>Draw circles to highlight UI elements</td></tr>
              <tr><td><strong>Rectangle</strong></td><td>Draw boxes around areas of interest</td></tr>
              <tr><td><strong>Arrow</strong></td><td>Point to specific elements with directional arrows</td></tr>
              <tr><td><strong>Text</strong></td><td>Add text labels and callouts anywhere on the screenshot</td></tr>
              <tr><td><strong>Blur</strong></td><td>Blur out sensitive information (passwords, personal data, etc.)</td></tr>
            </tbody>
          </table>
          <p className="doc-p">Annotations are saved per-step and appear in all views, including exports.</p>

          {/* ── Workflow Flowchart ── */}
          <h2 className="doc-section-heading" id="dash-workflow">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
            </span>
            Workflow Flowchart
          </h2>
          <p className="doc-p">Every guide includes an interactive <strong>visual process flow diagram</strong>. This gives you a bird's-eye view of the entire workflow.</p>

          <h3 className="doc-h3" id="dash-workflow-layout-options">Layout Options</h3>
          <table className="doc-table">
            <thead><tr><th>Layout</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Minimized</strong> (default)</td><td>Compact wrapped horizontal layout for dense guides.</td></tr>
              <tr><td><strong>Domain Based</strong></td><td>Groups steps by domain/application in vertical columns. Best for multi-site workflows.</td></tr>
              <tr><td><strong>Vertical</strong></td><td>Top-to-bottom linear flow.</td></tr>
              <tr><td><strong>Horizontal</strong></td><td>Left-to-right linear flow.</td></tr>
            </tbody>
          </table>

          <h3 className="doc-h3" id="dash-workflow-features">Flowchart Features</h3>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Labels</strong> &mdash; Each node shows a concise, action-focused summary written by InstaGuide</li>
            <li className="doc-li"><strong>Color-Coded Nodes</strong> &mdash; Purple for clicks, amber for keyboard, green for scroll</li>
            <li className="doc-li"><strong>Domain Headers</strong> &mdash; Domain-based layout shows site favicons and domain names</li>
            <li className="doc-li"><strong>App Change Detection</strong> &mdash; Visual indicators when the workflow switches between applications</li>
            <li className="doc-li"><strong>Click-to-Navigate</strong> &mdash; Click any flowchart node to scroll to the corresponding step card</li>
            <li className="doc-li"><strong>Zoom &amp; Pan</strong> &mdash; Full interactive controls for navigating large flowcharts</li>
          </ul>

          <div className="doc-callout info">
            <strong>Generate Workflow</strong> Click the <strong>&ldquo;Generate Workflow Summaries&rdquo;</strong> button on a guide to have InstaGuide create action labels and detect application changes for the flowchart.
          </div>

          {/* ── Projects & Folders ── */}
          <h2 className="doc-section-heading" id="dash-projects">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" /></svg>
            </span>
            Projects &amp; Folders
          </h2>
          <p className="doc-p">Organize your guides into <strong>projects</strong> (folder-like structure) for better management:</p>

          <h3 className="doc-h3" id="dash-projects-explorer">Projects Explorer</h3>
          <ul className="doc-ul">
            <li className="doc-li">Browse all projects in a sidebar, with guides displayed in the main area</li>
            <li className="doc-li">Auto-selects the first project when you open the explorer</li>
            <li className="doc-li">Bulk selection mode (via settings icon) lets you select multiple guides</li>
            <li className="doc-li">Move guides between projects, or bulk-delete with confirmation</li>
          </ul>

          <h3 className="doc-h3" id="dash-projects-managing">Managing Projects</h3>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Create Project</strong> &mdash; Click "New Project" and enter a name and optional description</li>
            <li className="doc-li"><strong>Edit Project</strong> &mdash; Update project name and description</li>
            <li className="doc-li"><strong>Delete Project</strong> &mdash; Removes the project (guides are not deleted, they become unassigned)</li>
            <li className="doc-li"><strong>Assign Guides</strong> &mdash; Move or bulk-assign guides to any project</li>
          </ul>
          <p className="doc-p">Personal accounts can create projects too &mdash; they're not limited to organizations.</p>

          {/* ── Favorites & Recent Guides ── */}
          <h2 className="doc-section-heading" id="dash-favorites">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" /></svg>
            </span>
            Favorites &amp; Recent Guides
          </h2>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Favorites</strong> &mdash; Star any guide to pin it to your favorites section. Persisted to the database so they sync across devices.</li>
            <li className="doc-li"><strong>Recent Guides</strong> &mdash; The last 20 guides you viewed are tracked automatically. Also stored in the database with localStorage fallback.</li>
          </ul>

          {/* ── Guide Sharing ── */}
          <h2 className="doc-section-heading" id="dash-sharing">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" /></svg>
            </span>
            Guide Sharing
          </h2>
          <p className="doc-p">Share guides with other members of your organization:</p>
          <ol className="doc-steps">
            <li>Open a guide and click the <strong>Share</strong> button.</li>
            <li>Select an organization member from the list.</li>
            <li>Choose a permission level: <strong>View</strong> (read-only) or <strong>Edit</strong> (can modify guide, steps, and annotations).</li>
            <li>The shared user will see the guide in their <strong>&ldquo;Shared with me&rdquo;</strong> section.</li>
          </ol>
          <p className="doc-p">You can update or revoke sharing permissions at any time.</p>

          {/* ── Comments ── */}
          <h2 className="doc-section-heading" id="dash-comments">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
            </span>
            Comments
          </h2>
          <p className="doc-p">Add comments to guides for team discussion and feedback:</p>
          <ul className="doc-ul">
            <li className="doc-li">Open any guide and scroll to the comments section</li>
            <li className="doc-li">Type your comment and submit</li>
            <li className="doc-li">Edit or delete your own comments</li>
            <li className="doc-li">Comments include the author name and timestamp</li>
          </ul>

          {/* ── Improvements & Suggestions ── */}
          <h2 className="doc-section-heading" id="dash-improvements">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="#fff" stroke="none"><path d="M11 3 L13 8.5 L18.5 10.5 L13 12.5 L11 18 L9 12.5 L3.5 10.5 L9 8.5 Z"/></svg>
            </span>
            Improvements &amp; Suggestions
          </h2>
          <div className="doc-callout success">
            <strong>Auto-correction &amp; review</strong> is included on every plan, including Free. Insta runs the polish pass right after every recording — see below for what it catches.
          </div>
          <p className="doc-p">Every time you finish capturing a guide, Insta reviews it for the kinds of cleanups you'd otherwise do by hand &mdash; duplicate clicks, vague descriptions, monotonous voice, places where a section break would help readers, and more. Deterministic fixes (exact duplicates, casing &amp; punctuation, clear app transitions) get applied silently right after capture. Everything subjective shows up in the <strong>Improvements panel</strong> as a card you can accept or reject.</p>

          <p className="doc-p">What Insta catches:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Adjacent-duplicate merges</strong> &mdash; multiple clicks on the same UI element (sticky modals, double-clicks, accidental repeats) collapse into one step with a rewritten description and action label.</li>
            <li className="doc-li"><strong>Generic-description rewrites</strong> &mdash; boilerplate like "Click on the element" or "Press the button" gets rewritten with real workflow context.</li>
            <li className="doc-li"><strong>Empty / placeholder action titles</strong> &mdash; steps with no title (or just "Click" / "Press") get a concrete 2&ndash;5 word imperative label. Also catches mid-state captures like <code>Click on "CreateLoading"</code> where the button text changed during the click.</li>
            <li className="doc-li"><strong>Context-aware rewrites</strong> &mdash; short mechanical descriptions ("Click Save.") get rewritten to reference the prior step's outcome and the form or screen the user is inside.</li>
            <li className="doc-li"><strong>Voice normalization</strong> &mdash; first-person ("I clicked…"), third-person narrative ("The user clicks…"), passive voice ("The form was submitted"), and demonstrative-narrator openers ("This opens…", "This shows…") rewrite to active outcome-led voice.</li>
            <li className="doc-li"><strong>Scroll-noise merge</strong> &mdash; runs of consecutive scrolls with no real action between them collapse to the final scroll position.</li>
            <li className="doc-li"><strong>Section breaks</strong> &mdash; app transitions (Salesforce &rarr; Outlook) and long runs without dividers get a proposed section divider with a Title Case label.</li>
            <li className="doc-li"><strong>Casing &amp; punctuation</strong> &mdash; sentence-case the first letter, add a terminal period. Local detector, no AI involved.</li>
          </ul>

          <p className="doc-p">How the panel works:</p>
          <ul className="doc-ul">
            <li className="doc-li">A purple banner appears at the top of the guide detail page once Insta finishes its first-pass review. It shows the count and a breakdown by type (merges, rewrites, voice, section breaks).</li>
            <li className="doc-li">Click <strong>Review</strong> to open the side panel. Each suggestion is a card with the current text, the proposed text, and a reason explaining why Insta thinks the change helps.</li>
            <li className="doc-li">Accept or reject each card individually, or use <strong>Accept all</strong> / <strong>Reject all</strong> / <strong>Reset</strong> for bulk decisions.</li>
            <li className="doc-li">Click <strong>Apply changes</strong> at the bottom to commit your decisions in one atomic batch &mdash; the guide updates, the change history logs a single summary entry, and the panel closes.</li>
            <li className="doc-li">On older guides (or after manual edits), click the purple <strong>Ask Insta to analyze</strong> button to re-run the review. The guide summary regenerates in parallel.</li>
          </ul>

          <div className="doc-callout info">
            Insta also auto-refreshes the guide summary after you delete, insert, or reorder steps &mdash; so the description on the guide intro card stays in sync with the actual step list. Runs in the background about 15 seconds after the last edit; bulk edits coalesce into one regeneration.
          </div>

          {/* ── Ask Insta Chat ── */}
          <h2 className="doc-section-heading" id="dash-ask-insta">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            </span>
            Ask Insta Chat
          </h2>
          <p className="doc-p">A persistent AI chatbot lives on the right edge of every authenticated page as a purple tab with a chat-bubble + sparkle icon. Click the tab to open a side panel where you can ask natural-language questions about your guides &mdash; <em>"How do I create a new customer in Salesforce?"</em>, <em>"Show me the steps for processing a refund."</em></p>
          <p className="doc-p">How it works:</p>
          <ul className="doc-ul">
            <li className="doc-li">Insta searches across your guide library, ranks the most relevant guides, and quotes from them in its answer.</li>
            <li className="doc-li">Answers include direct links to the guides &mdash; click any link to jump to the source.</li>
            <li className="doc-li">Multi-turn conversation: follow-up questions reuse context from earlier in the same chat.</li>
            <li className="doc-li">Click <strong>Clear chat</strong> inside the panel to start fresh.</li>
          </ul>
          <div className="doc-callout info">
            Ask Insta only sees <em>your</em> guides &mdash; not anyone else's, and not the wider internet. Public docs at <strong>docs.instaguide.ai</strong> have a separate Insta that answers questions about the product itself. Organization admins can also configure Ask Insta to route through your own AI provider (Organization Administration &rarr; AI Provider).
          </div>

          {/* ── Exporting Guides ── */}
          <h2 className="doc-section-heading" id="dash-export">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
            </span>
            Exporting Guides
          </h2>
          <p className="doc-p">Export your guides for offline use or sharing outside the platform. Available on <strong>paid plans only</strong>.</p>

          <div className="doc-card-grid">
            <div className="doc-card">
              <h4 className="doc-h4">HTML Export</h4>
              <ul className="doc-ul">
                <li className="doc-li">Self-contained HTML file (audio + images embedded)</li>
                <li className="doc-li">Dark mode toggle</li>
                <li className="doc-li">Progress tracking with checkboxes</li>
                <li className="doc-li">Collapsible steps</li>
                <li className="doc-li">Interactive table of contents</li>
                <li className="doc-li">Screenshot zoom modal</li>
                <li className="doc-li">Audio notes play inline</li>
                <li className="doc-li">All step types render (click, rich text, callouts, images, audio, section dividers)</li>
                <li className="doc-li">Domain-based process flow diagram</li>
                <li className="doc-li">Click indicators and annotations</li>
              </ul>
            </div>
            <div className="doc-card">
              <h4 className="doc-h4">PDF Export</h4>
              <ul className="doc-ul">
                <li className="doc-li">A4 format with 15mm margins</li>
                <li className="doc-li">Auto portrait/landscape orientation</li>
                <li className="doc-li">Domain-based process flow includes every step type with distinct colors</li>
                <li className="doc-li">Process flow nodes never split across page breaks</li>
                <li className="doc-li">Audio notes show the auto-generated transcript (PDFs can't play audio)</li>
                <li className="doc-li">Full annotations and click indicators</li>
                <li className="doc-li">InstaGuide-generated workflow summaries</li>
                <li className="doc-li">Color-coded event type badges</li>
                <li className="doc-li">Guide name used as filename</li>
              </ul>
            </div>
          </div>
          <p className="doc-p">Both export formats include the <strong>domain-based process flow</strong>, step screenshots with annotations, click indicators, and AI descriptions. The process flow now includes manual step types (rich text, callouts, images, audio, section dividers) inline with click steps, each with a distinct color so the type is readable at a glance while the step numbers stay sequential.</p>

          {/* ── Embedding Guides ── */}
          <h2 className="doc-section-heading" id="dash-embed">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
            </span>
            Embedding Guides
          </h2>
          <p className="doc-p">Embed a guide as an interactive iframe inside your own help center, Notion page, knowledge base, or any HTML site. Anyone who lands on the page can step through the guide &mdash; <strong>no sign-in required</strong>. Available on the <strong>Enterprise</strong> plan.</p>

          <h3 className="doc-h3">How to enable</h3>
          <ol className="doc-ul">
            <li className="doc-li">Open the guide in the dashboard.</li>
            <li className="doc-li">Click <strong>Embed</strong> in the top-right action bar (next to Share / HTML / PDF).</li>
            <li className="doc-li">Click <strong>Enable embed</strong>. InstaGuide mints a signed URL tied to the guide.</li>
            <li className="doc-li">Copy the <strong>iframe snippet</strong> and paste it into your help center / Notion / docs site.</li>
          </ol>

          <h3 className="doc-h3">What viewers see</h3>
          <p className="doc-p">A minimal step-by-step viewer scoped to the guide content &mdash; step number badges, screenshots with click indicators, annotations, audio notes (playable inline), callouts, rich text, and the process-flow diagram. None of the dashboard chrome (sidebar, comments, edit controls) is exposed.</p>

          <h3 className="doc-h3">Customization</h3>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Theme</strong> &mdash; light or dark. Default is light; embedders can override via the <code>?theme=dark</code> URL param.</li>
            <li className="doc-li"><strong>Default height</strong> &mdash; suggested iframe height in pixels (200&ndash;4000).</li>
            <li className="doc-li"><strong>Hide process flow</strong> &mdash; collapse the diagram for narrow sidebars (also overridable via <code>?flow=hide</code>).</li>
            <li className="doc-li"><strong>Branding (Enterprise)</strong> &mdash; replace the InstaGuide bolt with your own logo URL and set a primary color.</li>
          </ul>

          <h3 className="doc-h3">Revoking a link</h3>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Regenerate link</strong> &mdash; mints a fresh token; the old URL stops working immediately.</li>
            <li className="doc-li"><strong>Disable embed</strong> &mdash; turns embedding off entirely; viewers see a "Guide unavailable" message.</li>
          </ul>
          <div className="doc-callout info">
            Embed URLs are public-with-token &mdash; anyone with the link can view. They're <em>not</em> tied to your authentication. If a guide is confidential, prefer in-app sharing (Share button) instead.
          </div>

          {/* ── Profile & Account ── */}
          <h2 className="doc-section-heading" id="dash-profile">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
            </span>
            Profile &amp; Account
          </h2>
          <p className="doc-p">Access your profile page from the sidebar navigation to manage your account settings.</p>
          <div className="doc-card">
            <h4 className="doc-h4">Account Information</h4>
            <ul className="doc-ul">
              <li className="doc-li"><strong>Name &amp; Email</strong> &mdash; View your account details</li>
              <li className="doc-li"><strong>Plan Badge</strong> &mdash; See your current subscription plan</li>
              <li className="doc-li"><strong>Account Type</strong> &mdash; Personal or Organization account</li>
            </ul>
          </div>
          <div className="doc-card">
            <h4 className="doc-h4">Change Password</h4>
            <ul className="doc-ul">
              <li className="doc-li">Enter your current password for verification</li>
              <li className="doc-li">Enter and confirm your new password (minimum 6 characters)</li>
              <li className="doc-li">Password is securely hashed with bcrypt</li>
            </ul>
          </div>

          {/* ── Two-Factor Authentication ── */}
          <h2 className="doc-section-heading" id="dash-2fa">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </span>
            Two-Factor Authentication
          </h2>
          <p className="doc-p">Two-factor authentication (2FA) adds a one-time code from an authenticator app on top of your password. It's <strong>mandatory</strong> for all web and desktop logins; the Chrome extension follows the same rule once you've enabled 2FA on the account. After your password verifies, InstaGuide issues a short-lived challenge token and asks for the 6-digit code &mdash; the full session token isn't issued until the code is correct.</p>

          <h3 className="doc-h3" id="dash-2fa-setup">First-Time Setup</h3>
          <ol className="doc-steps">
            <li>Open <strong>Profile &rarr; Security</strong>, or sign in with a brand-new account &mdash; you're routed into the setup flow automatically when 2FA isn't yet enabled.</li>
            <li>InstaGuide displays a QR code and a base32 secret. Scan the QR with any TOTP authenticator app &mdash; Google Authenticator, 1Password, Authy, Microsoft Authenticator, Bitwarden, Aegis. You can also paste the base32 string into a password manager that supports TOTP fields.</li>
            <li>Type the 6-digit code your app shows. The code rotates every 30 seconds &mdash; if it expires while you're typing, wait for the next one.</li>
            <li>InstaGuide enables 2FA, returns the session token, and displays your <strong>8 backup codes</strong>. Save them immediately (see below).</li>
          </ol>
          <div className="doc-callout warning">
            On the next login from any device, you'll be asked for a TOTP code before the dashboard loads. The challenge token expires after 5 minutes &mdash; if you walk away mid-setup, you'll start over.
          </div>

          <h3 className="doc-h3" id="dash-2fa-backup-codes">Backup Codes</h3>
          <p className="doc-p">When 2FA is enabled, InstaGuide generates eight single-use backup codes. They're shown <strong>once</strong>, in plaintext, on the setup confirmation screen &mdash; you cannot view them again. Each code works exactly like a 6-digit TOTP at sign-in time and is invalidated the moment it's used.</p>
          <ul className="doc-ul">
            <li className="doc-li">Save them to a password manager, an encrypted note, or paper in a safe. Anywhere your authenticator <em>isn't</em>.</li>
            <li className="doc-li">Used a few? Open <strong>Profile &rarr; Security &rarr; Regenerate backup codes</strong>. You'll need to enter a current TOTP code to confirm &mdash; the old set is invalidated and a fresh 8-code set is shown once.</li>
            <li className="doc-li">Backup codes are stored as bcrypt hashes server-side. We can't recover them for you if they're lost &mdash; rotate via the recovery paths below.</li>
          </ul>

          <h3 className="doc-h3" id="dash-2fa-recovery">Lost Your Authenticator?</h3>
          <p className="doc-p">If you can't get into your authenticator app and your backup codes are gone, use the recovery path that matches your account type:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Personal account</strong> &mdash; On the login screen, click <strong>Forgot 2FA</strong>. Enter your email; if the account exists and is still personal, we send a one-time recovery link (valid for 1 hour). Clicking the link wipes your 2FA secret and backup codes, and you're routed back through the setup flow on your next login. The endpoint is rate-limited and always returns the same generic success message, so it can't be used to enumerate accounts.</li>
            <li className="doc-li"><strong>Organization member</strong> &mdash; The self-service email path is silently disabled for accounts inside an organization. Ask your org owner or admin to open <strong>Organization Administration &rarr; Users</strong>, find your row, and click <strong>Reset 2FA</strong>. Your secret + backup codes are wiped and you fall back into the setup flow on next login. Admins cannot reset the owner's 2FA &mdash; contact InstaGuide support if the owner is locked out.</li>
            <li className="doc-li"><strong>Last-ditch</strong> &mdash; if you're an admin and the recovery channels are unreachable, our support team can clear 2FA on a verified account. Email <a href="mailto:support@instaguide.ai">support@instaguide.ai</a> from the address on file.</li>
          </ul>
          <div className="doc-callout info">
            None of the recovery paths reveal your old secret &mdash; they wipe it entirely. After recovery you'll scan a new QR code and get a fresh set of backup codes.
          </div>

          <h3 className="doc-h3" id="dash-2fa-disable">Disabling 2FA</h3>
          <p className="doc-p">Disabling 2FA on personal accounts is supported but not encouraged &mdash; turning it off downgrades your account security. To disable:</p>
          <ol className="doc-steps">
            <li>Open <strong>Profile &rarr; Security &rarr; Disable 2FA</strong>.</li>
            <li>Enter a current TOTP code (or a remaining backup code).</li>
            <li>InstaGuide wipes the secret + remaining backup codes. The next login is password-only.</li>
          </ol>
          <p className="doc-p">Organization members can't self-disable via this path &mdash; the org's security policy decides whether 2FA is required, and individual members can't opt out unilaterally. If you legitimately need 2FA off (e.g. changing devices), ask an admin to reset it instead.</p>

          {/* ═══════════ ORGANIZATION ═══════════ */}

          {/* ── Organization Setup ── */}
          <h2 className="doc-section-heading" id="org-setup">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>
            </span>
            Organization Setup
          </h2>
          <p className="doc-p">Organization accounts unlock team features. When you sign up, choose <strong>&ldquo;Organization&rdquo;</strong> as your account type and provide an organization name.</p>
          <p className="doc-p">Access organization settings via the <strong>settings icon</strong> in the dashboard navigation. The settings page has the following tabs:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Overview</strong> &mdash; Account stats, theme selection</li>
            <li className="doc-li"><strong>Users</strong> &mdash; Manage organization members</li>
            <li className="doc-li"><strong>Roles</strong> &mdash; Predefined and custom roles</li>
            <li className="doc-li"><strong>Projects</strong> &mdash; Create and manage projects, assign members</li>
            <li className="doc-li"><strong>AI Provider</strong> &mdash; Configure AI service for step descriptions</li>
          </ul>

          {/* ── Members & Invitations ── */}
          <h2 className="doc-section-heading" id="org-members">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>
            </span>
            Members &amp; Invitations
          </h2>

          <h3 className="doc-h3" id="org-members-inviting">Inviting Members</h3>
          <ol className="doc-steps">
            <li>Go to <strong>Organization Settings &rarr; Users</strong>.</li>
            <li>Click <strong>&ldquo;Invite Member&rdquo;</strong>.</li>
            <li>Enter the person's email address, select their role, and optionally add a message.</li>
            <li>An invitation email is sent with a signup link. The invitation expires after a set period.</li>
            <li>When the invitee clicks the link, their email and organization are pre-filled.</li>
          </ol>

          <h3 className="doc-h3" id="org-members-managing">Managing Members</h3>
          <ul className="doc-ul">
            <li className="doc-li">View all members and their roles</li>
            <li className="doc-li">Change a member's organization role (Owner, Admin, Member)</li>
            <li className="doc-li">View and cancel pending invitations</li>
          </ul>

          {/* ── Roles & Permissions ── */}
          <h2 className="doc-section-heading" id="org-roles">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            </span>
            Roles &amp; Permissions
          </h2>

          <h3 className="doc-h3" id="org-roles-predefined">Predefined Roles</h3>
          <table className="doc-table">
            <thead><tr><th>Role</th><th>Permissions</th></tr></thead>
            <tbody>
              <tr><td><strong>Admin</strong></td><td>Full access: view, create, edit, delete, share, export guides; manage projects and members</td></tr>
              <tr><td><strong>Editor</strong></td><td>View, create, edit, share, and export guides; manage projects</td></tr>
              <tr><td><strong>Viewer</strong></td><td>View and export guides only</td></tr>
            </tbody>
          </table>

          <h3 className="doc-h3" id="org-roles-custom">Custom Roles</h3>
          <p className="doc-p">Create custom roles with granular permissions:</p>
          <table className="doc-table">
            <thead><tr><th>Permission</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><span className="doc-code">guide_view</span></td><td>View guides and steps</td></tr>
              <tr><td><span className="doc-code">guide_create</span></td><td>Create new guides</td></tr>
              <tr><td><span className="doc-code">guide_edit</span></td><td>Edit guide details, steps, and annotations</td></tr>
              <tr><td><span className="doc-code">guide_delete</span></td><td>Delete guides</td></tr>
              <tr><td><span className="doc-code">guide_share</span></td><td>Share guides with other members</td></tr>
              <tr><td><span className="doc-code">guide_export</span></td><td>Export guides as HTML/PDF</td></tr>
              <tr><td><span className="doc-code">project_manage</span></td><td>Create, edit, and delete projects</td></tr>
              <tr><td><span className="doc-code">member_manage</span></td><td>Manage organization members and invitations</td></tr>
            </tbody>
          </table>

          {/* ── Project Management ── */}
          <h2 className="doc-section-heading" id="org-projects">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" /></svg>
            </span>
            Project Management
          </h2>
          <p className="doc-p">Organization admins can manage projects from <strong>Settings &rarr; Projects</strong>:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Create projects</strong> with name and description</li>
            <li className="doc-li"><strong>Assign members</strong> to specific projects with roles</li>
            <li className="doc-li"><strong>Default project</strong> &mdash; One project per organization is marked as default for new guides</li>
            <li className="doc-li"><strong>Project members</strong> &mdash; Add, update roles, or remove members from individual projects</li>
          </ul>

          {/* ── AI Provider Settings ── */}
          <h2 className="doc-section-heading" id="org-ai">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" /><path d="M8 12l3 3 5-5" /></svg>
            </span>
            AI Provider Settings
          </h2>
          <p className="doc-p">Choose which AI provider your organization uses to generate step descriptions and power Insta features. Go to <strong>Settings &rarr; AI Provider</strong>. Bring your own credentials on any provider you already have a contract with.</p>
          <p className="doc-p">InstaGuide supports four providers:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Claude (Anthropic)</strong> &mdash; vision + text; generates step descriptions directly from the captured screenshot.</li>
            <li className="doc-li"><strong>Azure OpenAI</strong> &mdash; for organizations standardized on Microsoft Azure; needs endpoint + deployment name + API key.</li>
            <li className="doc-li"><strong>Google Gemini</strong> &mdash; vision + text; pick a model like <code>gemini-flash-latest</code>.</li>
            <li className="doc-li"><strong>Snowflake Cortex</strong> &mdash; text-only models. Step descriptions are generated from the action label rather than the screenshot.</li>
          </ul>
          <p className="doc-p">After entering credentials, click <strong>Test connection</strong> to verify the provider responds correctly before saving. Failures surface as a clear inline banner instead of breaking mid-recording later.</p>

          <div className="doc-callout info">
            <strong>Organization-wide</strong> AI settings apply to all guides created by any member of the organization. Individual members do not need separate AI configuration. The same provider is used for step descriptions, Enhance with Insta on rich-text steps, workflow summaries, and the Ask Insta chat.
          </div>

          {/* ── Sensitive Data Redaction ── */}
          <h2 className="doc-section-heading" id="org-redaction">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            </span>
            Sensitive Data Redaction
          </h2>
          <p className="doc-p">Automatically blur sensitive information out of captured screenshots before they're saved &mdash; emails, phone numbers, SSNs, credit cards, API keys, and more. Works for both Chrome extension and Desktop App captures. <strong>Off by default</strong>; admins enable it from <strong>Organization Administration &rarr; Redaction</strong>.</p>

          <h3 className="doc-h3" id="org-redaction-what-gets-detected">What gets detected</h3>
          <p className="doc-p">Pick the categories you care about. Each group is independently toggleable, and you can drill into sub-toggles for finer control:</p>
          <div className="doc-card-grid">
            <div className="doc-card" style={{ borderTop: '3px solid #6366f1' }}>
              <h4 className="doc-h4">Contact info</h4>
              <ul className="doc-ul">
                <li className="doc-li">Email addresses</li>
                <li className="doc-li">Phone numbers</li>
              </ul>
            </div>
            <div className="doc-card" style={{ borderTop: '3px solid #6366f1' }}>
              <h4 className="doc-h4">Government IDs</h4>
              <ul className="doc-ul">
                <li className="doc-li">US Social Security numbers</li>
                <li className="doc-li">Driver's license numbers</li>
              </ul>
            </div>
            <div className="doc-card" style={{ borderTop: '3px solid #6366f1' }}>
              <h4 className="doc-h4">Financial</h4>
              <ul className="doc-ul">
                <li className="doc-li">Credit card numbers</li>
                <li className="doc-li">IBAN &amp; bank account numbers</li>
              </ul>
            </div>
            <div className="doc-card" style={{ borderTop: '3px solid #6366f1' }}>
              <h4 className="doc-h4">Credentials</h4>
              <ul className="doc-ul">
                <li className="doc-li">API keys (Stripe, AWS, GitHub, Google OAuth, OpenAI, Anthropic)</li>
                <li className="doc-li">JWT tokens</li>
                <li className="doc-li">Private keys</li>
                <li className="doc-li">URL secret parameters</li>
              </ul>
            </div>
          </div>

          <h3 className="doc-h3" id="org-redaction-modes">Redaction modes</h3>
          <p className="doc-p">Choose how revealed information is handled:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Permanent</strong> (every plan) &mdash; the redacted version replaces the original. Everyone who views the guide sees the same image. Best choice for public guides and embeds.</li>
            <li className="doc-li"><strong>Permission-based</strong> (Pro and Enterprise) &mdash; viewers see the redacted version by default, but anyone whose role carries the <strong>View original (redacted)</strong> permission gets a green <strong>Reveal original</strong> button on each redacted step. Org owners and the guide's creator can always reveal; project members need the permission granted on their role; people who only have a direct share link cannot reveal.</li>
          </ul>

          <h3 className="doc-h3" id="org-redaction-style">Redaction style</h3>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Gaussian blur</strong> &mdash; keeps the field's shape visible so readers know what kind of value was there. Good default for most guides.</li>
            <li className="doc-li"><strong>Black rectangle</strong> &mdash; hides the region completely. Use this for the highest level of redaction.</li>
          </ul>

          <h3 className="doc-h3" id="org-redaction-badge">"Redacted (N)" badge</h3>
          <p className="doc-p">Any step where something was redacted shows a small <strong>Redacted (N)</strong> badge in its header. Hover the badge to see which categories were detected &mdash; categories only, never the actual values. If you're in Permission-based mode and have reveal access, the green <strong>Reveal original</strong> toggle sits right next to the badge.</p>

          <h3 className="doc-h3" id="org-redaction-extension">Configure from the recording surface too</h3>
          <p className="doc-p">The Chrome extension surfaces the same redaction settings inside its <strong>Capture Settings</strong> dialog (the gear icon on the floating panel or side panel). Org admins can flip the master switch and individual categories on the spot, without leaving the page they're recording. Members of the org see a read-only status (so they know what will and won't be captured) plus a link to the dashboard for changes.</p>

          <div className="doc-callout info">
            <strong>What doesn't reveal:</strong> public embedded guides always serve the redacted version regardless of mode &mdash; the public viewer has no concept of viewer permissions. Same for direct guide-share links shared outside your project.
          </div>

          <div className="doc-callout warning">
            <strong>Applies to new captures only.</strong> Redaction runs at the moment a step is saved, so it only affects steps recorded <em>after</em> you turn it on. Existing screenshots in older guides are unchanged.
          </div>

          {/* ── Storage Provider (Enterprise) ── */}
          <h2 className="doc-section-heading" id="org-storage">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
            </span>
            Storage Provider <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', marginLeft: 8 }}>· Enterprise</span>
          </h2>
          <p className="doc-p">By default, screenshots and audio notes live in our database. Enterprise organizations can optionally point new captures at their own cloud bucket &mdash; useful for compliance, data-residency requirements, or wanting to keep recording artefacts inside an audit-controlled environment.</p>
          <div className="doc-callout info">
            <strong>Optional, additive.</strong> The default option keeps storage in our DB &mdash; nothing changes unless you actively pick a provider. Configuration is restricted to org owners and admins.
          </div>

          <h3 className="doc-h3" id="org-storage-providers">Supported Providers</h3>
          <ul className="doc-ul">
            <li className="doc-li"><strong>AWS S3</strong> &mdash; region, bucket, access key ID, secret access key, optional key prefix.</li>
            <li className="doc-li"><strong>Azure Blob Storage</strong> &mdash; account name, account key, container, optional blob prefix.</li>
            <li className="doc-li"><strong>Google Cloud Storage</strong> &mdash; bucket, service account key (JSON), optional project ID and object prefix.</li>
          </ul>

          <h3 className="doc-h3" id="org-storage-configure">How to Configure</h3>
          <ol className="doc-ul">
            <li className="doc-li">Go to <strong>Organization Administration &rarr; Storage Provider</strong>.</li>
            <li className="doc-li">Click the tile for your provider and fill in the credentials. Secrets are masked when read back, so you can re-save other fields without re-typing the secret.</li>
            <li className="doc-li">Click <strong>Test connection</strong>. InstaGuide uploads a tiny probe object to verify the credentials and bucket permissions, then deletes it. Failures show a clear error so you can fix credentials before going live.</li>
            <li className="doc-li">Click <strong>Save Settings</strong>. From that moment on, every new screenshot and audio note uploaded by your team goes directly to your bucket; we only store the resulting URL in our database.</li>
          </ol>
          <div className="doc-callout warning">
            <strong>New captures only.</strong> Existing guides are not migrated. Screenshots and audio captured before you enabled BYO storage stay in our database. Contact support if you need a one-time backfill.
          </div>

          <h3 className="doc-h3" id="org-storage-bucket-policy">Bucket Permissions</h3>
          <p className="doc-p">For HTML and PDF exports to display screenshots and play audio notes, the uploaded objects must be readable by the recipient's browser. The simplest setup is to allow public-read on the prefix InstaGuide uses (configurable in the form). Alternatively, front your bucket with a CDN that proxies the read path. Either way, no listing access is needed.</p>

          {/* ═══════════ BILLING & SUBSCRIPTIONS ═══════════ */}

          {/* ── Plans & Pricing ── */}
          <h2 className="doc-section-heading" id="billing-plans">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
            </span>
            Plans &amp; Pricing
          </h2>
          <p className="doc-p">InstaGuide offers <strong>personal plans</strong> (Free, Starter, Pro &mdash; self-serve via Stripe Checkout), the <strong>Business</strong> org plan (signup-driven with a 30-day trial), and <strong>Enterprise</strong> (sales-led, configured per organization). Every new personal signup starts with a 15-day Pro trial; every new org signup starts with a 30-day Business trial &mdash; no credit card required.</p>

          <h3 className="doc-h3" id="billing-plans-personal">Personal Plans</h3>
          <table className="doc-table">
            <thead><tr><th>Plan</th><th>Price</th><th>Guides</th><th>Steps / guide</th><th>Key Features</th></tr></thead>
            <tbody>
              <tr><td><strong>Free</strong></td><td>$0</td><td>10 lifetime</td><td>15</td><td>Recording, step titles + descriptions + summaries, auto-correction &amp; review, dashboard</td></tr>
              <tr><td><strong>Starter</strong></td><td>$13/mo (or annual)</td><td>25 lifetime</td><td>25</td><td>Free, plus PDF + HTML export, process flow diagrams</td></tr>
              <tr><td><strong>Pro</strong></td><td>$29/mo (or annual)</td><td>50 lifetime</td><td>50</td><td>Starter, plus voice narration, Ask Insta Assistant, change tracking, <strong>Meeting Mode (10 uploads / month)</strong></td></tr>
            </tbody>
          </table>
          <p className="doc-p">Free, Starter, and Pro are <strong>single-user plans</strong> — no team members, no guide sharing, no project collaboration. The guide count is a <strong>lifetime cap</strong> on the user's account (delete a guide, free up a slot). The step-per-guide cap is shown on the Stripe Checkout page before you confirm. If you need to share guides or collaborate with teammates, see the Business plan below.</p>

          <h3 className="doc-h3" id="billing-plans-business">Business (Organizations)</h3>
          <p className="doc-p"><strong>Business is $39 per user per month, minimum 5 users.</strong> It's the team tier and includes everything in Pro plus:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Guide sharing &amp; teams</strong> &mdash; org-wide library, role-based access, project folders.</li>
            <li className="doc-li"><strong>Meeting Mode</strong> &mdash; 10 recording uploads per user per month, pooled across the org.</li>
            <li className="doc-li"><strong>Priority support</strong>.</li>
            <li className="doc-li"><strong>50 guides per user</strong>, pooled org-wide. A 5-user team has up to 250 total guides; a 10-user team up to 500. Cap grows automatically as you add licenses. 75 steps per guide.</li>
          </ul>
          <p className="doc-p">Business does <em>not</em> include custom branding, SSO/SAML, BYO cloud storage, or a custom AI provider &mdash; those move to Enterprise. New org signups get a 30-day Business trial automatically; conversion is via account team.</p>

          <h3 className="doc-h3" id="billing-plans-enterprise">Enterprise (Organizations)</h3>
          <p className="doc-p">Enterprise is for larger orgs that need custom branding, SSO/SAML, BYO cloud storage, or a custom AI provider. Pricing uses a <strong>split-license model</strong>:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Builder seats</strong> &mdash; can record, edit, and publish guides. Listed at $50 per user per month (configurable per contract).</li>
            <li className="doc-li"><strong>Viewer seats</strong> &mdash; can read and search guides, leave comments, but cannot create or edit. Listed at $5 per user per month.</li>
            <li className="doc-li">Minimum 10 seats total. Most orgs end up with a small Builder team and a large Viewer rollout.</li>
            <li className="doc-li">Includes everything in Business, plus <strong>custom branding</strong> (logo + primary color on embedded guides), SSO/SAML, BYO cloud storage (S3 / Azure Blob / GCS), BYO AI provider, and unlimited steps per guide.</li>
            <li className="doc-li"><strong>Guide creation is effectively unlimited</strong>, subject to a fair-use monthly cap: <strong>50 guides per Builder license per month</strong> by default, resets on the 1st of each UTC month. Cap scales with license count (10 Builders = 500 / month, 100 Builders = 5,000 / month). Contact your account manager to raise the cap for high-volume workloads. Enterprise BYO &mdash; where you provide your own AI keys &mdash; has no monthly cap since you absorb the AI cost directly. <strong>Meeting Mode</strong> imports are subject to the same fair-use cap.</li>
          </ul>
          <p className="doc-p">Contact <a href="mailto:hello@instaguide.ai">hello@instaguide.ai</a> for a quote. Enterprise plans are configured by InstaGuide admins inside the License Admin panel &mdash; the public pricing page surfaces a <em>Contact sales</em> CTA rather than a Stripe Checkout button.</p>

          <h3 className="doc-h3" id="billing-plans-trial">Free trials</h3>
          <p className="doc-p">Trials are automatic on signup &mdash; nothing to configure, no credit card needed:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Personal accounts</strong> get a <strong>15-day Pro trial</strong>. After day 15, the account moves to the Free plan unless you upgrade.</li>
            <li className="doc-li"><strong>Organization accounts</strong> get a <strong>30-day Business trial</strong> with no minimum-license enforcement during the trial. After day 30, the account moves to the Free plan unless you upgrade.</li>
            <li className="doc-li">A reminder banner appears from day 8 onward and re-shows every 3 days until you upgrade or dismiss it. Personal-account banners switch to an "urgent" style in the last 3 days.</li>
            <li className="doc-li">Support can extend a trial from the License Admin panel if you need more time to evaluate.</li>
          </ul>

          {/* ── Upgrading Your Plan ── */}
          <h2 className="doc-section-heading" id="billing-upgrade">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M5 12l7-7 7 7"/><path d="M12 19V5"/></svg>
            </span>
            Upgrading Your Plan
          </h2>
          <p className="doc-p">Personal accounts upgrade directly &mdash; no sales call. Pick a plan on the in-app pricing page or click the upgrade CTA in any plan-limit dialog.</p>

          <h3 className="doc-h3" id="billing-upgrade-checkout">Stripe Checkout</h3>
          <ol className="doc-steps">
            <li>Open <strong>Pricing</strong> from the sidebar or click <strong>View Plans</strong> on any plan-limit warning.</li>
            <li>Pick monthly or annual billing (annual gives a discount). Click <strong>Upgrade to Starter</strong> or <strong>Upgrade to Pro</strong>.</li>
            <li>Stripe Checkout opens in a new tab with your email pre-filled. Enter payment details &mdash; Stripe handles the entire transaction; InstaGuide never sees raw card data.</li>
            <li>On success, Stripe redirects to <code>/billing/success</code> and a webhook updates your account in the background. The plan badge in the dashboard updates within a few seconds &mdash; refresh if it doesn't appear immediately.</li>
          </ol>
          <div className="doc-callout info">
            Your plan reflects the latest webhook event from Stripe &mdash; not what you clicked. If billing genuinely failed after Checkout (declined card, fraud check), the webhook never fires and your plan stays where it was. Watch for an email from Stripe in that case.
          </div>

          <h3 className="doc-h3" id="billing-upgrade-banner">Reminder Banner</h3>
          <p className="doc-p">A purple banner appears at the top of every dashboard page when one of two conditions triggers:</p>
          <ul className="doc-ul">
            <li className="doc-li"><strong>Free plan ≥ 80% of guide limit</strong> &mdash; warns you're approaching the lifetime cap. Goes red at 100%.</li>
            <li className="doc-li"><strong>Subscription canceling within 14 days</strong> &mdash; reminds you the cancel-at-period-end is approaching. The exact renewal / cancel date is shown.</li>
          </ul>
          <p className="doc-p">Each banner has a <strong>Dismiss</strong> control with a per-reminder cooldown (12&ndash;24 hours) so it doesn't follow you across every page. Organization members who aren't the org owner don't see the banner &mdash; only the owner is in a position to act on billing.</p>

          {/* ── Managing Your Subscription ── */}
          <h2 className="doc-section-heading" id="billing-portal">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.6 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.6a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
            </span>
            Managing Your Subscription
          </h2>
          <p className="doc-p">After upgrading, all subscription management &mdash; changing your card, downloading invoices, switching plans, or canceling &mdash; happens inside the <strong>Stripe-hosted billing portal</strong>. InstaGuide doesn't reimplement these flows; Stripe does them well and they stay PCI-compliant by definition.</p>

          <h3 className="doc-h3" id="billing-portal-launch">Opening the Portal</h3>
          <ol className="doc-steps">
            <li>Open <strong>Profile</strong> from the sidebar.</li>
            <li>Scroll to <strong>Subscription</strong> and click <strong>Manage subscription</strong>.</li>
            <li>InstaGuide generates a Stripe portal session and redirects you. The session is signed to your Stripe customer ID &mdash; only you can act on it; the URL won't work for anyone else.</li>
          </ol>
          <p className="doc-p">Inside the portal you can:</p>
          <ul className="doc-ul">
            <li className="doc-li">Download every invoice as PDF.</li>
            <li className="doc-li">Update the card on file &mdash; the change applies to all future charges.</li>
            <li className="doc-li">Switch between monthly and annual billing on the same plan, or upgrade Starter &rarr; Pro / downgrade Pro &rarr; Starter. Pro-rated automatically.</li>
            <li className="doc-li">Update billing address and VAT / tax ID for invoices.</li>
          </ul>

          <h3 className="doc-h3" id="billing-portal-cancel">Canceling a Subscription</h3>
          <p className="doc-p">Cancellations are <strong>end-of-period</strong>, not immediate. Click <strong>Cancel subscription</strong> in the portal; InstaGuide marks <code>cancel_at_period_end = true</code>. You keep premium features until your current billing period ends &mdash; <em>no proration, no refund</em> for the remainder of the period you've already paid for. After that, your account quietly drops back to Free (Starter or Pro features become unavailable; your guides stay intact).</p>
          <p className="doc-p">Changed your mind? Re-open the portal before the period ends and click <strong>Renew subscription</strong>. Auto-renewal is restored; the cancel flag is cleared.</p>
          <div className="doc-callout info">
            If your card declines on renewal, Stripe retries on a schedule and emails you. After the retries are exhausted, the subscription transitions to <em>past_due</em>; the webhook lands and your plan drops to Free until the payment method is fixed and the subscription resumes.
          </div>

          {/* ═══════════ REFERENCE ═══════════ */}

          {/* ── Keyboard Shortcuts ── */}
          <h2 className="doc-section-heading" id="keyboard-shortcuts">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10" /></svg>
            </span>
            Keyboard Shortcuts
          </h2>
          <p className="doc-p">The extension captures the following keyboard shortcuts during recording:</p>

          <table className="doc-table">
            <thead><tr><th>Shortcut</th><th>Action</th></tr></thead>
            <tbody>
              <tr><td><span className="doc-kbd">Enter</span></td><td>Submit / Confirm</td></tr>
              <tr><td><span className="doc-kbd">Escape</span></td><td>Cancel / Close</td></tr>
              <tr><td><span className="doc-kbd">Ctrl</span> + <span className="doc-kbd">S</span></td><td>Save</td></tr>
              <tr><td><span className="doc-kbd">Ctrl</span> + <span className="doc-kbd">Z</span></td><td>Undo</td></tr>
              <tr><td><span className="doc-kbd">Ctrl</span> + <span className="doc-kbd">F</span></td><td>Find</td></tr>
              <tr><td><span className="doc-kbd">Ctrl</span> + <span className="doc-kbd">P</span></td><td>Print</td></tr>
              <tr><td><span className="doc-kbd">Ctrl</span> + <span className="doc-kbd">A</span></td><td>Select All</td></tr>
            </tbody>
          </table>
          <p className="doc-p">Regular typing (letters, numbers) is intentionally <strong>not captured</strong> to avoid noise and protect sensitive input.</p>

          {/* ── Troubleshooting ── */}
          <h2 className="doc-section-heading" id="troubleshooting">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01" /></svg>
            </span>
            Troubleshooting
          </h2>

          <div className="doc-trouble-card">
            <h4 className="doc-h4">Extension not capturing clicks</h4>
            <ul className="doc-ul">
              <li className="doc-li">Make sure you clicked <strong>&ldquo;Start Recording&rdquo;</strong> and entered a guide name</li>
              <li className="doc-li">Check that the floating panel is visible (look for a small icon in the page corner)</li>
              <li className="doc-li">Some pages (e.g., Chrome internal pages, the Chrome Web Store) cannot be recorded &mdash; this is a browser security restriction</li>
              <li className="doc-li">Try reloading the page, then start recording again</li>
            </ul>
          </div>
          <div className="doc-trouble-card">
            <h4 className="doc-h4">AI descriptions not generating</h4>
            <ul className="doc-ul">
              <li className="doc-li">Verify your organization's AI provider settings are configured correctly in Settings</li>
              <li className="doc-li">Check that the API key is valid and has sufficient credits/quota</li>
              <li className="doc-li">Contact your administrator if the issue persists</li>
            </ul>
          </div>
          <div className="doc-trouble-card">
            <h4 className="doc-h4">Steps appear out of order</h4>
            <ul className="doc-ul">
              <li className="doc-li">Use the <strong>&ldquo;Renumber Steps&rdquo;</strong> action on the guide detail page to fix numbering</li>
              <li className="doc-li">You can also drag-and-drop steps to reorder them manually</li>
            </ul>
          </div>
          <div className="doc-trouble-card">
            <h4 className="doc-h4">Cannot export guides</h4>
            <ul className="doc-ul">
              <li className="doc-li">Exports require a <strong>paid plan</strong> (Starter or above)</li>
              <li className="doc-li">Free plan users will see an upgrade prompt when attempting to export</li>
            </ul>
          </div>
          <div className="doc-trouble-card">
            <h4 className="doc-h4">Voice narration not working</h4>
            <ul className="doc-ul">
              <li className="doc-li">Make sure you picked <strong>"Allow visiting this site"</strong> (not "Allow this time") when Chrome's microphone prompt appeared. The session-only grant doesn't carry into the background audio capture &mdash; recording will look like it's working but no audio gets transcribed.</li>
              <li className="doc-li">If you accidentally picked "Allow this time" or "Block", reset the permission: open <code>chrome://settings/content/microphone</code>, find the InstaGuide extension entry, remove it, then click the mic button again to re-trigger the prompt.</li>
              <li className="doc-li">Ensure no other application is using the microphone exclusively.</li>
              <li className="doc-li">Voice transcription happens <strong>after each step is captured</strong> (not in real-time as you speak). The yellow transcript bar appears under the step in the side panel within a couple of seconds of capturing it.</li>
              <li className="doc-li">Voice narration requires a <strong>paid plan</strong> (Starter or above). Free accounts can record but transcription is skipped.</li>
              <li className="doc-li">Contact your administrator if transcription is still not working.</li>
            </ul>
          </div>
          <div className="doc-trouble-card">
            <h4 className="doc-h4">Extension popup shows "Not Logged In"</h4>
            <ul className="doc-ul">
              <li className="doc-li">Click the login link and enter your credentials</li>
              <li className="doc-li">If you were previously logged in, your session may have expired (extension sessions last 30 days) &mdash; log in again</li>
              <li className="doc-li">Check your internet connection and try again</li>
            </ul>
          </div>

          {/* ── FAQ ── */}
          <h2 className="doc-section-heading" id="faq">
            <span className="doc-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
            </span>
            Frequently Asked Questions
          </h2>

          {[
            { q: 'Can I edit descriptions generated by InstaGuide?', a: 'Yes. Click any step description in the guide detail view to edit it inline. Your edits are saved immediately.' },
            { q: 'Are my screenshots stored securely?', a: 'Screenshots are stored securely in the database. Access is controlled by authentication and guide ownership. Shared guides respect view/edit permissions. All data exports are sanitized to prevent injection attacks.' },
            { q: 'How long do login sessions last?', a: 'Web dashboard sessions are automatically refreshed every hour while you are active (mouse, keyboard, or click activity). After 60 minutes of inactivity, you will be logged out automatically. Tokens are securely blacklisted on logout. Desktop app sessions last 24 hours with automatic refresh. Chrome Extension sessions remain valid for 30 days.' },
            { q: 'Can I use InstaGuide without an internet connection?', a: 'The Chrome Extension requires an internet connection to capture and generate AI descriptions. For offline capture, use the InstaGuide Desktop App, which stores data locally and syncs when you reconnect.' },
            { q: 'How many steps can a guide have?', a: 'There is no hard limit on the number of steps per guide. However, very large guides (100+ steps) may load more slowly. Consider splitting complex workflows into multiple guides organized in a project folder.' },
            { q: 'Can I move guides between projects?', a: 'Yes. Use the guide\'s move action or the bulk selection mode in the Projects Explorer to move guides between projects.' },
            { q: 'What browsers are supported?', a: 'The Chrome Extension works on Google Chrome and Chromium-based browsers (Edge, Brave, etc.). The web dashboard works in any modern browser.' },
            { q: 'Can I delete my account?', a: 'Contact your system administrator to delete an account. Organization owners should transfer ownership before requesting deletion.' },
            { q: 'How do I switch AI providers?', a: 'Organization admins can switch providers from Settings \u2192 AI Provider. Select the new provider, enter the required credentials, and save. All future guides will use the new provider for generating step descriptions.' },
          ].map((item, idx) => (
            <div key={idx} className={`doc-faq-item ${faqOpen[idx] ? 'open' : ''}`}>
              <button className="doc-faq-question" onClick={() => toggleFaq(idx)}>
                {item.q}
                <span className="doc-faq-icon">+</span>
              </button>
              <div className="doc-faq-answer">
                <div className="doc-faq-answer-inner">{item.a}</div>
              </div>
            </div>
          ))}

        </div>{/* end doc-main-inner */}
      </div>{/* end doc-main */}

      {/* ═══════════ RIGHT PANEL ═══════════ */}
      <div className="doc-right-panel">
        {/* Current group's sibling sections */}
        <div className="doc-right-title">{activeGroup}</div>
        {(() => {
          const group = SECTIONS.find(s => s.group === activeGroup);
          if (!group) return null;
          return group.items.map(item => (
            <React.Fragment key={item.id}>
              <button
                className={`doc-right-link ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => scrollToSection(item.id)}
              >
                {item.title}
              </button>
              {/* Show sub-sections under active item */}
              {activeSection === item.id && SUB_SECTIONS[item.id] && SUB_SECTIONS[item.id].map(sub => (
                <button
                  key={sub.id}
                  className="doc-right-link sub"
                  onClick={() => scrollToSection(sub.id)}
                >
                  {sub.title}
                </button>
              ))}
            </React.Fragment>
          ));
        })()}
      </div>

      {/* ═══════════ ASK INSTA PANEL ═══════════ */}
      {askInstaOpen && (
        <>
          <div className="doc-ask-overlay" onClick={() => setAskInstaOpen(false)} />
          <div className="doc-ask-panel">
            <div className="doc-ask-header">
              <div className="doc-ask-header-left">
                <span className="doc-ask-iconwrap" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                  </svg>
                  <svg className="doc-ask-sparkle" width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 3 L13 8.5 L18.5 10.5 L13 12.5 L11 18 L9 12.5 L3.5 10.5 L9 8.5 Z" />
                  </svg>
                </span>
                Ask Insta
              </div>
              <button className="doc-ask-close" onClick={() => setAskInstaOpen(false)}>&times;</button>
            </div>
            <div className="doc-ask-messages">
              {chatMessages.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 12px', display: 'block', opacity: 0.4 }}>
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Ask me anything about InstaGuide</div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>I can help with features, troubleshooting, and more.</div>
                </div>
              )}
              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`doc-ask-msg ${msg.role}`}
                  dangerouslySetInnerHTML={msg.role === 'assistant' ? { __html: renderMarkdown(msg.content) } : undefined}
                >
                  {msg.role === 'user' ? msg.content : undefined}
                </div>
              ))}
              {chatLoading && (
                <div className="doc-ask-typing">
                  <div className="doc-ask-typing-dot" />
                  <div className="doc-ask-typing-dot" />
                  <div className="doc-ask-typing-dot" />
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div className="doc-ask-input-bar">
              <input
                ref={chatInputRef}
                className="doc-ask-input"
                type="text"
                placeholder="Ask a question..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') sendChatMessage(); }}
              />
              <button
                className="doc-ask-send"
                onClick={sendChatMessage}
                disabled={!chatInput.trim() || chatLoading}
              >
                Send
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
